import { readLanguage } from '@webbuilder/i18n'
import { markOfferLabelsStale } from './offerLabelsEvents'
import { notifyConfigAttentionChanged } from './attentionSignal'
import { notifyBookingsChanged } from './bookingSignal'
import { notifyDashboardItemRead } from './dashboardReadSignal'
import { NOTES_MAX } from './projectNotes'
import type { LimitedResource } from '@webbuilder/plans'
import type { ProjectAccessRole, ProjectRole } from '@webbuilder/shared-types'
import { supabase } from './supabase'
/* Del paquete y no de `lib/i18n`: ése importa de aquí y se haría un ciclo. */
import { compressImageForUpload, faststartMp4, prepareImageUpload, uploadFormDataWithProgress, videoPosterFile } from '@webbuilder/media'
import type { BlogPost, BlogSettings, OfferCustomField, OfferFields, OfferTemplateKind, SectionCopy, TemplateAiIssue, TemplateFacets } from '@webbuilder/schemas'
import type { StyleRecipe } from '@webbuilder/theme'
import type { OfferPageStyle } from '@webbuilder/theme'

/* Exportado porque `/geo` vive FUERA de `/api` —se pregunta sin sesión— y no
   pasa por `request`, que es lo que resuelve esta dirección para todo lo demás. */
export const WORKER_URL = (import.meta.env.VITE_WORKER_URL as string | undefined) ?? 'http://localhost:8787'
// The professional library is global production content. Local dashboard work
// can target the production media API without sending every other API call there.
const IMAGE_LIBRARY_WORKER_URL = (import.meta.env.VITE_IMAGE_LIBRARY_WORKER_URL as string | undefined)
  ?? WORKER_URL
export const EDITOR_URL = (import.meta.env.VITE_EDITOR_URL as string | undefined)
  ?? (window.location.hostname.endsWith('veltica.com') ? 'https://editor.veltica.com' : 'http://localhost:5173')
export const PUBLIC_SITE_DOMAIN = (import.meta.env.VITE_PUBLIC_SITE_DOMAIN as string | undefined) ?? 'veltica.com'

/**
 * La dirección de un sitio publicado en su subdominio.
 *
 * El `https://` iba fijo en cada sitio que arma esta URL, así que trabajando en
 * local «Ver sitio» salía a internet: publicabas contra la base de Docker y el
 * enlace abría el Worker de producción, que no conoce ese proyecto. En local el
 * sitio vive en `algo.localhost:8787` y habla http.
 */
export function publicSiteUrl(subdomain: string): string {
  const scheme = PUBLIC_SITE_DOMAIN.split(':')[0] === 'localhost' ? 'http' : 'https'
  return `${scheme}://${subdomain}.${PUBLIC_SITE_DOMAIN}`
}


export type Project = {
  id: string
  name: string
  slug: string
  /** Qué eres en él. Ausente hablando con un worker anterior al equipo. */
  role?: ProjectAccessRole
  /** Si es de otra persona y te lo comparten. */
  shared?: boolean
  /** El favicon del sitio: la cara del proyecto en el selector. Vacío si no
   *  hay, y ausente hablando con un worker anterior al campo. */
  faviconLight?: string
  faviconDark?: string
  /** Apuntes del widget Notas. Ausente en workers anteriores a la columna. */
  notes?: string
  thumbnailUrl: string | null
  /**
   * IANA zone deciding where this project's analytics day boundary falls.
   * Optional: absent when talking to a worker older than the field.
   */
  timezone?: string
  publishedAt: string | null
  publishedSite?: {
    subdomain: string
    publishedAt: string
  } | null
  createdAt: string
  updatedAt: string
}

export type AdminTemplate = {
  id: string
  name: string
  slug: string
  kind: string
  category: string
  description: string | null
  status: string
  styleTags: string[]
  colorTags: string[]
  aiTags: string[]
  facets: TemplateFacets
  isPremium: boolean
  isPublic: boolean
  thumbnailUrl: string | null
  createdAt?: string
  updatedAt?: string
  publishedAt?: string | null
  /** Cuántas veces lo han insertado los clientes. */
  usesCount?: number
  /** Draft differs from the published version (edits saved but not yet live). */
  hasUnpublishedChanges?: boolean
  /** Qué le falta para que la IA lo use, leído del borrador (ver `templateAiReadiness`). */
  aiReadiness?: { ready: boolean; blocking: TemplateAiIssue[]; advice: TemplateAiIssue[] }
  /** El diseño de página (blog, CMS) del que es un bloque: no se inserta suelto. */
  partOf?: { id: string; name: string; category: string }
}

export type AdminTemplatePayload = {
  name: string
  slug: string
  kind: 'website' | 'page' | 'section' | 'header' | 'footer' | 'component'
  category: string
  description?: string
  styleTags?: string[]
  colorTags?: string[]
  aiTags?: string[]
  facets?: TemplateFacets
  isPremium?: boolean
  isPublic?: boolean
  thumbnailUrl?: string | null
}

export type ProfessionalImage = {
  id: string
  title: string
  url: string
  storageKey: string | null
  hashtags: string[]
  altText: string
  mimeType: string | null
  sizeBytes: number | null
  /** Sólo los vídeos: su portada (ver `libraryPosterUrl` en el worker). */
  posterUrl?: string | null
  createdAt: string
  updatedAt: string
}

export type PublicTemplate = {
  id: string
  name: string
  slug: string
  category: string
  description?: string | null
  thumbnailUrl: string | null
  isPremium: boolean
  kind: string
  status: string
  /** Para qué industrias es (`facets.bestFor`). */
  bestFor?: string[]
}

export type PublicTemplateData = {
  elements: unknown[]
  theme?: unknown
}

/**
 * Resolve the freshest access token. `getSession()` transparently refreshes the
 * JWT when it's near/after expiry, so we never send a stale token the worker would
 * reject with 401. Falls back to the caller-supplied token if the session read fails.
 */
async function freshToken(fallback?: string): Promise<string | undefined> {
  try {
    const { data } = await supabase.auth.getSession()
    return data.session?.access_token ?? fallback
  } catch {
    return fallback
  }
}

// De-dupe concurrent refreshes: many in-flight requests can 401 at once, and calling
// refreshSession() N times would rotate the refresh token N times and race itself.
// A single shared promise means one refresh serves every waiter.
let refreshInFlight: Promise<string | null> | null = null
function forceRefresh(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = supabase.auth
      .refreshSession()
      .then(({ data }) => data.session?.access_token ?? null)
      .catch(() => null)
      .finally(() => { refreshInFlight = null })
  }
  return refreshInFlight
}

function doFetch(path: string, token: string, options?: RequestInit, baseUrl = WORKER_URL): Promise<Response> {
  return fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options?.headers,
    },
  })
}

async function request<T>(path: string, token: string, options?: RequestInit, baseUrl = WORKER_URL): Promise<T> {
  let authToken = (await freshToken(token)) ?? token
  let res = await doFetch(path, authToken, options, baseUrl)

  // A 401 almost always means the access token expired between refresh ticks
  // (e.g. the laptop slept). Force one refresh and retry before surfacing an error.
  if (res.status === 401) {
    const refreshed = await forceRefresh()
    if (refreshed) {
      authToken = refreshed
      res = await doFetch(path, authToken, options, baseUrl)
    }
  }

  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new Error(`${res.status}: ${body}`)
  }
  // 204 No Content (e.g. DELETE) has no body — don't try to parse JSON.
  if (res.status === 204) return undefined as T
  const json = (await res.json()) as { data: T }
  return json.data
}

/**
 * El motivo que dio el servidor, sacado del error de `request`.
 *
 * `request` lanza `Error('403: {"message":"..."}')` porque conserva el cuerpo
 * entero, que es lo correcto para depurar. Para pantalla hace falta la frase de
 * dentro: los mensajes de estas rutas distinguen «ya está invitado» de «tu plan
 * no da para más», y son acciones distintas. Un texto genérico las tapa las dos
 * —y tapa también los fallos de verdad, como una tabla que no existe—.
 */
/**
 * El código que mandó el servidor, si lo mandó.
 *
 * Con él, el panel dice el error en SU idioma en vez de repetir la frase que
 * llegó ya escrita —que viene siempre en español, y en un panel en inglés se
 * lee como un fallo del producto. Se lee con una expresión regular por lo mismo
 * que `serverMessage`: el cuerpo puede venir envuelto por Hono y aquí sólo se
 * pregunta por una marca. Ver `errors.ts` en el worker.
 */
export function serverErrorCode(err: unknown): string | null {
  const raw = err instanceof Error ? err.message : ''
  return /"code":"([a-zA-Z.]+)"/.exec(raw)?.[1] ?? null
}

/**
 * Con qué rellenar los huecos de ese texto.
 *
 * Un error como el del cupo de invitados dice «Tu plan permite {limit}
 * invitados»: el número lo sabe el worker y lo manda aparte, porque la frase
 * traducida no es la misma que la suya. Sin esto, quien llenaba su equipo leía
 * la llave y el nombre de la variable en mitad de la pantalla.
 *
 * Sólo números y palabras dentro del objeto, y se descarta entero si no encaja:
 * lo que llega es texto de un error, y un `JSON.parse` de cualquier cosa dentro
 * de un `catch` convierte un mensaje feo en una pantalla en blanco.
 */
export function serverErrorVars(err: unknown): Record<string, string | number> | undefined {
  const raw = err instanceof Error ? err.message : ''
  const found = /"vars":(\{[^{}]*\})/.exec(raw)?.[1]
  if (!found) return undefined
  try {
    const parsed: unknown = JSON.parse(found)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined
    const vars: Record<string, string | number> = {}
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === 'string' || typeof value === 'number') vars[key] = value
    }
    return Object.keys(vars).length > 0 ? vars : undefined
  } catch {
    return undefined
  }
}

export function serverMessage(err: unknown, fallback: string): string {
  const raw = err instanceof Error ? err.message : ''
  return /\{"message":"([^"]+)"/.exec(raw)?.[1]
    ?? /\{"error":"([^"]+)"/.exec(raw)?.[1]
    ?? /^\d+:\s*(.+)$/.exec(raw)?.[1]?.slice(0, 200)
    ?? fallback
}

/**
 * Si el servidor rechazó esto porque el cupo del plan está lleno.
 *
 * El 402 de `enforceProjectLimit` y compañía viaja como un JSON dentro del texto
 * del error, que es lo que `request` conserva. Leerlo con una expresión regular
 * y no parsearlo es a propósito: el cuerpo puede venir envuelto por Hono, y aquí
 * sólo se pregunta por dos marcas.
 *
 * Con `resource` se distingue de qué cupo se habla: quien crea un proyecto sólo
 * quiere abrir la ventana de mejorar plan si el que se llenó es el suyo, no si
 * lo que faltaba eran créditos de IA.
 */
export function isPlanLimitError(err: unknown, resource?: string): boolean {
  const raw = err instanceof Error ? err.message : ''
  if (!raw.startsWith('402:') || !raw.includes('"plan_limit"')) return false
  return resource === undefined || raw.includes(`"resource":"${resource}"`)
}

// ─── Equipo de un proyecto ───────────────────────────────────────────────────

export type TeamPerson = {
  id: string
  email: string
  name: string
  avatarUrl: string | null
}

export type TeamMember = TeamPerson & {
  memberId: string
  role: ProjectRole
  joinedAt: string
}

export type TeamInvite = {
  id: string
  email: string
  role: ProjectRole
  expiresAt: string
  createdAt: string
}

export type TeamSnapshot = {
  owner: TeamPerson & { role: 'owner' }
  members: TeamMember[]
  invites: TeamInvite[]
  /** El rol de quien mira, para saber qué botones tienen sentido. */
  myRole: ProjectAccessRole
  /** Y quién es, para reconocer su propia fila. */
  myUserId: string
  seats: { plan: string; limit: number | null; used: number }
}

export function fetchTeam(projectId: string, token: string): Promise<TeamSnapshot> {
  return request<TeamSnapshot>(`/api/projects/${projectId}/team`, token)
}

/** Devuelve el token UNA vez: es el respaldo si el correo no llega. */
export function inviteToTeam(
  projectId: string,
  token: string,
  body: { email: string; role: ProjectRole },
): Promise<TeamInvite & { token: string }> {
  return request(`/api/projects/${projectId}/team/invites`, token, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export function cancelTeamInvite(projectId: string, inviteId: string, token: string): Promise<{ ok: boolean }> {
  return request(`/api/projects/${projectId}/team/invites/${inviteId}`, token, { method: 'DELETE' })
}

export function changeTeamRole(
  projectId: string,
  memberId: string,
  role: ProjectRole,
  token: string,
): Promise<{ ok: boolean }> {
  return request(`/api/projects/${projectId}/team/members/${memberId}`, token, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  })
}

export function removeTeamMember(
  projectId: string,
  memberId: string,
  token: string,
): Promise<{ ok: boolean; left: boolean }> {
  return request(`/api/projects/${projectId}/team/members/${memberId}`, token, { method: 'DELETE' })
}

export type TeamInviteOffer = {
  projectName: string
  role: ProjectRole
  /* Claves, no texto. El servidor no sabe en qué idioma está mirando quien
     recibe la invitación; el panel sí. Mandaba «Diseño» hecho y derecho, así que
     el rol salía en español dentro de un panel en inglés. */
  roleLabelKey: string
  roleSummaryKey: string
  capabilities: string[]
  fromName: string
  email: string
  expiresAt: string
}

export function fetchTeamInvite(inviteToken: string, token: string): Promise<TeamInviteOffer> {
  return request(`/api/team-invites/${inviteToken}`, token)
}

export function acceptTeamInvite(
  inviteToken: string,
  token: string,
): Promise<{ projectId: string; role: ProjectRole }> {
  return request(`/api/team-invites/${inviteToken}/accept`, token, { method: 'POST' })
}

export function rejectTeamInvite(inviteToken: string, token: string): Promise<{ ok: boolean }> {
  return request(`/api/team-invites/${inviteToken}/reject`, token, { method: 'POST' })
}

/** Una invitación pendiente dirigida a mí, vista desde mi panel. */
export type MyTeamInvite = {
  id: string
  projectId: string
  projectName: string
  role: ProjectRole
  /** La clave de la etiqueta del rol; la pinta el panel en su idioma. */
  roleLabelKey: string
  /** Quien invitó, como se le conoce. Vacío si no se pudo resolver. */
  fromName: string
  expiresAt: string
  createdAt: string
}

/** Las invitaciones vivas dirigidas a mi correo, para enseñarlas sin el enlace. */
export function fetchMyTeamInvites(token: string): Promise<MyTeamInvite[]> {
  return request('/api/team-invites', token)
}

/**
 * Aceptar o rechazar desde el panel, por id y sin el enlace del correo.
 *
 * El id no autoriza nada por sí solo: el servidor sigue exigiendo que el correo
 * de la cuenta sea el de la invitación, igual que por el enlace.
 */
export function acceptMyTeamInvite(inviteId: string, token: string): Promise<{
  projectId: string
  role: ProjectRole
}> {
  return request(`/api/team-invites/mine/${inviteId}/accept`, token, { method: 'POST' })
}

export function rejectMyTeamInvite(inviteId: string, token: string): Promise<{ ok: boolean }> {
  return request(`/api/team-invites/mine/${inviteId}/reject`, token, { method: 'POST' })
}

/** Una página de resultados: los datos y lo que hace falta para pintar el paginador. */
export type Paged<T> = { data: T[]; page: number; pageSize: number; total: number }

/**
 * Como `request`, pero conserva el sobre entero.
 *
 * `request` devuelve sólo `data`, que es lo correcto para un recurso suelto.
 * Una lista paginada necesita además el total y la página, así que aquí no se
 * desenvuelve.
 */
async function requestPage<T>(path: string, token: string): Promise<Paged<T>> {
  let authToken = (await freshToken(token)) ?? token
  let res = await doFetch(path, authToken, undefined, WORKER_URL)
  if (res.status === 401) {
    const refreshed = await forceRefresh()
    if (refreshed) {
      authToken = refreshed
      res = await doFetch(path, authToken, undefined, WORKER_URL)
    }
  }
  if (!res.ok) throw new Error(`${res.status}: ${await res.text().catch(() => '')}`)
  return (await res.json()) as Paged<T>
}

const pageQuery = (params: Record<string, string | number | undefined>) => {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value))
  }
  const query = search.toString()
  return query ? `?${query}` : ''
}

export async function listProjects(token: string) {
  return request<Project[]>('/api/projects', token)
}

export type UserRole = 'user' | 'designer' | 'admin'

export function getAccountAccess(token: string) {
  return request<{ userId: string; role: UserRole; isAdmin: boolean }>('/api/me', token)
}

/** Lo que hay que resolver o saber antes de eliminar la cuenta. */
export type AccountDeletionPreflight = {
  graceDays: number
  projects: number
  domains: { id: string; hostname: string; expiresAt: string | null; transferable: boolean }[]
  teamProjects: { id: string; name: string; members: number }[]
  futureBookings: number
  customerSubscriptions: number
  openDisputes: number
  purchasePending: boolean
}

export function getAccountDeletionPreflight(token: string) {
  return request<AccountDeletionPreflight>('/api/account/deletion-preflight', token)
}

/** Programa la eliminación. Cada dominio registrado aquí lleva su decisión. */
export function scheduleAccountDeletion(token: string, domains: Record<string, 'transfer' | 'expire'>) {
  return request<{ purgeAt: string; graceDays: number }>('/api/account', token, {
    method: 'DELETE',
    body: JSON.stringify({ domains }),
  })
}

/** Deshace una eliminación pendiente. */
export function restoreAccount(token: string) {
  return request<{ restored: boolean; projects: number }>('/api/account/restore', token, { method: 'POST' })
}

/** Create a one-time token that gives the editor its own independent session. */
export function createEditorHandoff(token: string) {
  return request<{ tokenHash: string }>('/api/auth/editor-handoff', token, { method: 'POST' })
}

export type AccountUsage = {
  bandwidthProjects?: { projectId: string; name: string; views: number; bandwidthBytes: number }[]
  plan: BillingPlan
  limits: { ai_credits: number | null; ai_tokens: number | null; storage_bytes: number | null; bandwidth_bytes: number | null; form_submissions: number | null }
  /** El cupo de proyectos que aplica el servidor: el del plan, o el de admin. */
  projectLimit?: number | null
  used: { aiCredits: number; aiTokens: number; storageBytes: number; bandwidthBytes: number; formSubmissions: number }
  /** Cuándo se reponen las generaciones de sitio. `null` en el plan gratis, donde son en total. */
  aiCreditsResetAt: string | null
  /** ISO timestamp for the next UTC daily AI-token boundary. */
  aiTokensResetAt: string
  /** ISO timestamp for the next UTC calendar-month form counter boundary. */
  formSubmissionsResetAt?: string
}

/** Lo que el buscador de la barra encuentra dentro del proyecto. */
export type ProjectSearchResults = {
  contacts: Array<{ id: string; name: string; email: string }>
  cmsItems: Array<{ id: string; title: string; collectionId: string; collectionName: string }>
  offers: Array<{ id: string; title: string; status: string }>
  pages: Array<{ pageId: string; name: string; slug: string }>
}

/**
 * Contenido del proyecto que casa con lo escrito.
 *
 * Cuatro de cada tipo como mucho: esto alimenta un atajo, no una pantalla de
 * resultados. Ver la ruta en el worker.
 */
export function searchProjectContent(token: string, projectId: string, query: string) {
  return request<ProjectSearchResults>(`/api/projects/${projectId}/search?q=${encodeURIComponent(query)}`, token)
}

/** Real usage + plan limits for the metered metrics (AI, storage, …). */
export function getAccountUsage(token: string) {
  return request<AccountUsage>('/api/account/usage', token)
}

/** Pending email change, only while within the 10-min window since the code was sent. */
export function getPendingEmailChange(token: string) {
  return request<{ pendingEmail: string | null; sentAt: string | null; expiresAt: string | null }>('/api/account/email-change', token)
}

/** Abandon a pending email change (makes the "Descartar" banner action stick). */
export function cancelPendingEmailChange(token: string) {
  return request<{ ok: boolean }>('/api/account/email-change', token, { method: 'DELETE' })
}

/**
 * The browser already knows the user's zone, so we send it instead of asking.
 * It decides where the analytics day boundary falls, and is editable later in
 * the workspace settings.
 */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/** Tope de los apuntes. El mismo que valida el servidor y la propia base. */
/** Lo guardado ahora mismo, para fundirlo con lo propio antes de escribir. */
export function getProjectNotes(token: string, projectId: string) {
  return request<{ notes: string }>(`/api/projects/${projectId}/notes`, token)
}

/**
 * Guarda los apuntes, o falla — pero nunca manda un JSON a medias.
 *
 * Antes recortaba con `notes.slice(0, NOTES_MAX)`. Con las notas en texto plano
 * eso perdía el final de una frase; con el array JSON de ahora parte la
 * estructura, el servidor acepta los 20 000 caracteres exactos que le llegan, y
 * al releerlo TODAS las notas se convierten en una sola con el JSON crudo
 * dentro. El tope se comprueba antes de llamar aquí, con `notesFit`.
 */
export function updateProjectNotes(token: string, projectId: string, notes: string) {
  if (notes.length > NOTES_MAX) {
    return Promise.reject(new Error(`notes too long: ${notes.length} > ${NOTES_MAX}`))
  }
  return request<{ ok: true }>(`/api/projects/${projectId}/notes`, token, {
    method: 'PATCH',
    body: JSON.stringify({ notes }),
  })
}

/**
 * El enlace para entrar al sitio publicado como su dueño.
 *
 * Lleva un token de vida corta que el servidor de sitios cambia por una cookie
 * del propio dominio del sitio; con ella aparece la barra de administración. Es
 * un rodeo necesario: la sesión vive aquí y el sitio en otro dominio, y ninguno
 * de los dos puede leer la del otro.
 */
export function getProjectAdminLink(token: string, projectId: string) {
  return request<{ url: string; origin: string; token: string; param: string }>(
    `/api/projects/${projectId}/admin-link`,
    token,
    { method: 'POST' },
  )
}

/**
 * Abre el sitio publicado —o una de sus páginas— como su dueño.
 *
 * La pestaña se abre antes de esperar a la red: hacerlo después la bloquea el
 * navegador, porque ya no viene de un clic. Si el enlace firmado falla, se abre
 * la dirección tal cual, que es lo que se hacía antes de que existiera la barra.
 */
export async function openPublishedAsOwner(token: string, projectId: string, url: string) {
  /*
   * Sin `noopener` al abrir la pestaña vacía, y a propósito.
   *
   * `window.open` con `noopener` devuelve SIEMPRE `null` —cortar la referencia
   * es justo lo que hace—, pero la pestaña se abre igual. Así que no había forma
   * de llevarla a ninguna parte y el respaldo abría una SEGUNDA: quedaban dos
   * ventanas, una en blanco y huérfana y la del sitio.
   *
   * Anular `opener` en cuanto existe da la misma protección —la página abierta
   * no puede alcanzar a ésta— conservando el asa para navegarla.
   */
  const tab = window.open('', '_blank')
  if (tab) tab.opener = null
  const go = (target: string) => {
    if (tab) tab.location.href = target
    /* Sólo si el navegador bloqueó la primera: ahí no hay pestaña que
       reutilizar, así que abrir otra no duplica nada. */
    else window.open(target, '_blank', 'noopener,noreferrer')
  }
  try {
    const link = await getProjectAdminLink(token, projectId)
    const target = new URL(url)
    target.searchParams.set(link.param, link.token)
    go(target.toString())
  } catch {
    go(url)
  }
}

/**
 * `business`: lo que el asistente preguntó del negocio (nombre comercial,
 * correo, teléfono, dirección). Opcional; va a «Información del negocio».
 */
/** `assetIds`: las fotos que subió en el asistente de IA, que pasan a ser de este proyecto. */
export function createProject(token: string, name: string, slug: string, data?: unknown, business?: Record<string, string>, subdomain?: string, assetIds?: string[]) {
  return request<Project>('/api/projects', token, {
    method: 'POST',
    body: JSON.stringify({ name, slug, data, timezone: browserTimezone(), language: readLanguage(), ...(business && Object.keys(business).length ? { business } : {}), ...(subdomain ? { subdomain } : {}), ...(assetIds?.length ? { assetIds } : {}) }),
  })
}

/**
 * La dirección de un proyecto nuevo: la que sale del nombre, o la escrita a mano
 * (`subdomain`). Dice si está libre (`available`, con el código si no) y, si no,
 * una que sí lo está (`subdomain`) para ofrecerla —nunca para ponerla sin
 * preguntar—. La que se crea es la que se manda en `createProject(..., subdomain)`.
 */
export function projectAddress(token: string, input: { name?: string; subdomain?: string }) {
  const query = input.subdomain ? `subdomain=${encodeURIComponent(input.subdomain)}` : `name=${encodeURIComponent(input.name ?? '')}`
  return request<{ requested: string; available: boolean; code: string | null; subdomain: string; url: string }>(`/api/projects/address?${query}`, token)
}

/** Con `language`, sólo los templates en ese idioma y los que no tienen. */
export function listPublicTemplates(token: string, kind?: string, language?: string) {
  const query = (kind ? `?limit=50&kind=${encodeURIComponent(kind)}` : '?limit=50') + (language ? `&language=${encodeURIComponent(language)}` : '')
  return request<PublicTemplate[]>(`/api/templates${query}`, token)
}

/** Un hueco de la página de la demo: su categoría, sus textos y sus variantes. */
export type DemoSlot = {
  category: string
  copy: SectionCopy
  variants: { id: string; name: string; thumbnailUrl: string | null }[]
  /** El lienzo de la primera variante. */
  elements: unknown[]
}

export type DemoPage = { language: 'es' | 'en'; brandName: string; slots: DemoSlot[] }

/**
 * La página de la demo pública (`/demo`), sin cuenta: por eso no pasa por
 * `request`, que pide un token. Ver `routes/demoPage.ts` en el worker.
 */
async function requestPublic<T>(path: string): Promise<T> {
  const res = await fetch(`${WORKER_URL}${path}`)
  if (!res.ok) throw new Error(`${res.status}: ${await res.text().catch(() => '')}`)
  return ((await res.json()) as { data: T }).data
}

export function getDemoPage(language: 'es' | 'en') {
  return requestPublic<DemoPage>(`/demo/page?language=${language}`)
}

/** La portada de veltica.com, la del proyecto conectado al dominio de la casa. */
export type DemoSite = {
  language: 'es' | 'en'
  brandName: string
  page: Record<string, unknown>
  projectTheme: unknown
  projectContent: unknown
  /** Los nodos de las secciones que se pueden agregar con el «+» (la página `/secciones`). */
  sections?: unknown[]
}

export function getDemoSite() {
  return requestPublic<DemoSite>('/demo/site')
}

/** Otra variante de un hueco de la demo: sólo las de la demo, también sin cuenta. */
export function getDemoBlock(id: string, language: 'es' | 'en') {
  return requestPublic<{ elements: unknown[] }>(`/demo/blocks/${encodeURIComponent(id)}?language=${language}`)
}

/** Con `language`, el lienzo con los textos del template en ese idioma, si los tiene. */
export function getPublicTemplateData(token: string, id: string, language?: string) {
  return request<PublicTemplateData>(`/api/templates/${id}/data${language ? `?language=${encodeURIComponent(language)}` : ''}`, token)
}

/** Client-safe published-site settings (favicons, badge, password state). */
export type SiteSettings = {
  faviconLightDataUrl: string
  faviconDarkDataUrl: string
  webclipDataUrl: string
  showVelticaBadge: boolean
  canRemoveVelticaBadge: boolean
  passwordProtected: boolean
  hasPassword: boolean
  seoTitle: string
  seoDescription: string
  indexable: boolean
  ogImageUrl: string
  /** Lo que empezó la IA y sigue «por completar»: «próximamente» (por defecto) u oculto. */
  pendingItems?: 'soon' | 'hide'
  /** «Usar carrito», ya resuelto. */
  cart?: 'on' | 'off'
  /** El restaurante en OpenTable del sitio (Configuración › OpenTable). */
  openTable?: { rids: string[] } | null
  /** Google Analytics del sitio (Configuración › Google Analytics). */
  googleAnalytics?: { measurementId: string } | null
  /** Cuentas de clientes en el sitio (`/account`). Apagadas por defecto. */
  customerAccounts?: 'on' | 'off' | 'auto'
}

/**
 * Las políticas legales del sitio.
 *
 * Vienen SIEMPRE las cuatro, escritas o no: el panel las pinta todas para que
 * se vea de un vistazo cuáles faltan, que es la mitad del valor de la pantalla.
 */
/**
 * Los datos del negocio que piden los documentos legales.
 *
 * `filled` y `total` los calcula el servidor: es lo que el panel enseña como
 * progreso y lo que explica por qué una plantilla todavía trae huecos.
 */
export type BusinessInfo = {
  tradeName: string
  legalName: string
  taxId: string
  address: string
  city: string
  state: string
  country: string
  email: string
  phone: string
  legalContact: string
  currency: string
}

export type BusinessInfoWithProgress = BusinessInfo & { filled: number; total: number }

export function getBusinessInfo(token: string, projectId: string) {
  return request<BusinessInfoWithProgress>(`/api/projects/${projectId}/business`, token)
}

// ─── La dirección de véltica ────────────────────────────────────────────────

export type SiteSubdomain = {
  /** La elegida. Null si nadie la ha elegido todavía. */
  subdomain: string | null
  /** La que está sirviendo ahora mismo, si el sitio está publicado. */
  published: string | null
  /** La respuesta a lo que se estaba comprobando, si se pidió. */
  /** `code`, para decirlo en su idioma (`error.<code>`); `reason`, la frase de red. */
  check: { value: string; available: boolean; code?: string | null; reason: string | null } | null
}

export function getSiteSubdomain(token: string, projectId: string, check?: string) {
  const query = check ? `?check=${encodeURIComponent(check)}` : ''
  return request<SiteSubdomain>(`/api/projects/${projectId}/subdomain${query}`, token)
}

/** Si la dirección ya sirve el sitio, o todavía se está aplicando el cambio. */
export type SiteSubdomainStatus = {
  subdomain: string | null
  /** Falso si el sitio nunca se publicó: entonces no hay nada que esperar. */
  published: boolean
  /** Cierto cuando escribir la dirección en el navegador ya abre el sitio. */
  serving: boolean
}

export function getSiteSubdomainStatus(token: string, projectId: string) {
  return request<SiteSubdomainStatus>(`/api/projects/${projectId}/subdomain/status`, token)
}

export function saveSiteSubdomain(token: string, projectId: string, subdomain: string) {
  return request<{ subdomain: string; moved: boolean }>(`/api/projects/${projectId}/subdomain`, token, {
    method: 'PATCH',
    body: JSON.stringify({ subdomain }),
  })
}

// ─── Traspaso de un proyecto a otra cuenta ──────────────────────────────────

/** La invitación viva de un proyecto, tal como la ve quien entrega. */
export type ProjectTransfer = {
  id: string
  toEmail: string
  status: string
  expiresAt: string
  createdAt: string
}

/** Qué recibe quien acepta. Se consulta ANTES de decidir. */
/**
 * Un tope que lo que llega no respeta.
 *
 * El servidor manda el recurso y las dos cifras; la frase la escribe el panel,
 * que sabe en qué idioma está mirando quien recibe el sitio. Antes llegaba «12
 * páginas (tu plan permite 3)» ya escrito en español, así que la pantalla del
 * traspaso salía a medias en un panel en inglés.
 */
export type PlanShortfall = { resource: LimitedResource; value: number; cap: number }

export type TransferOffer = {
  projectName: string
  expiresAt: string
  inventory: {
    pages: number
    collections: number
    cmsItems: number
    contacts: number
    forms: number
    messages: number
    images: number
    imageBytes: number
    offers: number
    orders: number
    /** Citas con fecha por delante: compromisos con personas reales. */
    upcomingBookings: number
    backups: number
    /** Cuántos invitados pierden el acceso al aceptar. El equipo se vacía. */
    team: number
    customDomain: string | null
    subdomain: string | null
    published: boolean
    /** Si quien entrega tenía cobrador. Se borra: el sitio deja de poder cobrar. */
    hadPayments: boolean
  }
  plan: 'free' | 'pro' | 'business'
  /** Si el plan de quien recibe cubre lo que llega. No bloquea: informa. */
  planFits: boolean
  /* Datos y no frases: el servidor no sabe en qué idioma mira quien recibe el
     sitio. Lo escribe el panel — ver `shortfallList`. */
  shortfalls: PlanShortfall[]
  graceDays: number
  /**
   * La portada, para dibujarla sin que el sitio esté publicado.
   *
   * `null` si el proyecto no tiene ninguna página con contenido. Viene dentro de
   * la oferta porque la autorización es la misma: quien mira no tiene acceso al
   * proyecto, sólo una invitación viva a su nombre.
   */
  cover: { name: string; elements: unknown[]; theme: unknown } | null
}

export function getProjectTransfer(token: string, projectId: string) {
  return request<ProjectTransfer | null>(`/api/projects/${projectId}/transfers`, token)
}

export function inviteProjectTransfer(token: string, projectId: string, email: string) {
  return request<{ id: string; token: string; expiresAt: string; email: string }>(
    `/api/projects/${projectId}/transfers`,
    token,
    { method: 'POST', body: JSON.stringify({ email }) },
  )
}

/**
 * Un traspaso que YO envié, con su desenlace.
 *
 * No se consulta por proyecto sino por quien lo mandó, y eso es justo lo que lo
 * hace útil: en cuanto el otro acepta, el proyecto deja de ser mío y la ruta
 * por proyecto me contesta 403. Ésta sobrevive al cambio de dueño.
 */
export type SentTransfer = {
  id: string
  projectId: string
  projectName: string
  toEmail: string
  /** Quién aceptó. `null` mientras no haya aceptado nadie. */
  toName: string | null
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled' | 'expired'
  expiresAt: string
  createdAt: string
  resolvedAt: string | null
  /**
   * Cuándo terminó el repaso posterior a aceptar.
   *
   * Aceptado no es terminado: quien recibe todavía tiene que conectar SU
   * cobrador y poner SUS datos legales. Mientras esto sea nulo, el sitio que
   * entregaste no puede cobrar.
   */
  handoverDoneAt: string | null
}

export function listSentTransfers(token: string) {
  return request<SentTransfer[]>('/api/transfers/sent', token)
}

/** Rehace el enlace y reinicia el plazo. El anterior deja de servir. */
export function resendProjectTransfer(token: string, projectId: string, transferId: string) {
  return request<{ id: string; token: string; expiresAt: string; email: string }>(
    `/api/projects/${projectId}/transfers/${transferId}/resend`,
    token,
    { method: 'POST' },
  )
}

export function cancelProjectTransfer(token: string, projectId: string, transferId: string) {
  return request<{ ok: true }>(`/api/projects/${projectId}/transfers/${transferId}`, token, { method: 'DELETE' })
}

/**
 * Lo que queda pendiente tras aceptar un traspaso.
 *
 * `null` cuando no hay ninguno: no haber recibido este proyecto de nadie es el
 * caso normal, y el panel pregunta por todos.
 */
export type Handover = {
  transferId: string
  projectName: string
  receivedAt: string | null
  payments: { sellingOffers: number; connected: boolean }
  business: { filled: number; total: number; tradeName: string; legalName: string }
  plan: { plan: BillingPlan; fits: boolean; shortfalls: PlanShortfall[]; graceUntil: string | null }
  customDomain: string | null
  upcomingBookings: number
}

export function getHandover(token: string, projectId: string) {
  return request<Handover | null>(`/api/projects/${projectId}/handover`, token)
}

export function finishHandover(token: string, projectId: string) {
  return request<{ ok: true }>(`/api/projects/${projectId}/handover/done`, token, { method: 'POST' })
}

export function getTransferOffer(token: string, inviteToken: string) {
  return request<TransferOffer>(`/api/transfers/${inviteToken}`, token)
}

/** Un traspaso pendiente dirigido a mí, visto desde mi panel. */
export type MyTransfer = {
  id: string
  projectId: string
  projectName: string
  /** Quien entrega, como se le conoce. Vacío si no se pudo resolver. */
  fromName: string
  expiresAt: string
  createdAt: string
}

/**
 * Los traspasos vivos dirigidos a mi correo.
 *
 * Es lo que permite enseñarlos en el panel sin depender del enlace del correo,
 * igual que con las invitaciones de equipo. Antes de esto, un traspaso sólo
 * existía si el correo llegaba y no se borraba.
 */
export function listMyTransfers(token: string): Promise<MyTransfer[]> {
  return request('/api/transfers/mine', token)
}

/* Las tres operaciones por id, para cuando el enlace no está a mano. El id no
   autoriza nada: el servidor sigue exigiendo que el correo de la cuenta sea el
   de la invitación. */
export function getMyTransferOffer(token: string, id: string) {
  return request<TransferOffer>(`/api/transfers/mine/${id}`, token)
}

export function acceptMyTransfer(token: string, id: string) {
  return request<{ ok: true; projectId: string; graceUntil: string | null; planFits: boolean; shortfalls: PlanShortfall[] }>(
    `/api/transfers/mine/${id}/accept`, token, { method: 'POST' },
  )
}

export function rejectMyTransfer(token: string, id: string) {
  return request<{ ok: true }>(`/api/transfers/mine/${id}/reject`, token, { method: 'POST' })
}

export function acceptTransfer(token: string, inviteToken: string) {
  return request<{ ok: true; projectId: string; graceUntil: string | null; planFits: boolean; shortfalls: PlanShortfall[] }>(
    `/api/transfers/${inviteToken}/accept`,
    token,
    { method: 'POST' },
  )
}

export function rejectTransfer(token: string, inviteToken: string) {
  return request<{ ok: true }>(`/api/transfers/${inviteToken}/reject`, token, { method: 'POST' })
}

/** Lo que queda a medias en Configuración, y si el sitio ya vende. */
export type SetupAttention = {
  /** Con una oferta activa hay dinero de por medio, y entonces sí se avisa. */
  sells: boolean
  policies: {
    unwritten: number
    holes: number
    /** Lo que quien compra tendría que poder leer antes de pagar y hoy no está publicado. */
    missingForSale?: { key: ProjectPolicy['key']; title: string }[]
  }
  business: { missing: number }
}

export function getSetupAttention(token: string, projectId: string) {
  return request<SetupAttention>(`/api/projects/${projectId}/setup-attention`, token)
}

/** Deja pasar la respuesta y avisa de que el aviso de Configuración cambió. */
function changedAttention(projectId: string) {
  return <T>(result: T): T => {
    notifyConfigAttentionChanged(projectId)
    return result
  }
}

export function saveBusinessInfo(token: string, projectId: string, patch: Partial<BusinessInfo>) {
  return request<BusinessInfoWithProgress>(`/api/projects/${projectId}/business`, token, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  }).then(changedAttention(projectId))
}

/**
 * Dónde opera el negocio y en qué moneda cobra.
 *
 * Un solo ajuste del que cuelgan tres cosas: la moneda de cada precio —aquí y
 * en Stripe—, cómo se escribe un importe en todo el panel, y qué plantilla legal
 * se ofrece en Políticas.
 *
 * Los catálogos viajan con la respuesta en vez de estar repetidos aquí: son la
 * misma lista que decide en qué moneda se crea un precio, y dos copias acaban
 * ofreciendo en la pantalla una moneda que el servidor rechaza al guardar.
 */
export type RegionCountry = {
  code: string
  name: string
  currency: string
  locale: string
  profile: 'mx' | 'es' | 'us' | 'generic'
  language: 'es' | 'en'
}

export type RegionCurrency = { code: string; name: string; symbol: string }

export type ProjectRegion = {
  countryCode: string
  countryName: string
  currency: string
  language: 'es' | 'en'
  /** Derivados del país: con qué formato se escribe un importe y qué ley se cita. */
  locale: string
  profile: RegionCountry['profile']
}

export type ProjectRegionSettings = ProjectRegion & {
  countries: RegionCountry[]
  currencies: RegionCurrency[]
  /** Cuántas ofertas vivas cambiarían de moneda. Lo que hay que avisar antes. */
  pricedOffers: number
  currenciesInUse: string[]
  stripeConnected: boolean
}

/** Lo que se movió al guardar: cuántos precios se reetiquetaron y qué falló. */
export type RegionSaveResult = ProjectRegion & {
  repriced: { offers: number; prices: number; failed: number }
}

export function getProjectRegion(token: string, projectId: string) {
  return request<ProjectRegionSettings>(`/api/projects/${projectId}/region`, token)
}

export function saveProjectRegion(
  token: string,
  projectId: string,
  patch: Partial<Pick<ProjectRegion, 'countryCode' | 'currency' | 'language'>>,
) {
  return request<RegionSaveResult>(`/api/projects/${projectId}/region`, token, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  }).then(changedAttention(projectId))
}

export type ProjectPolicy = {
  key: 'privacidad' | 'terminos' | 'reembolsos' | 'cancelaciones' | 'envios' | 'aviso-legal'
  title: string
  /** La CLAVE del catálogo, no el texto: lo dice el panel. Ver `POLICY_HINT_KEYS`. */
  hintKey: string
  /** Borrador con el que empezar. No es asesoría legal y el panel lo dice. */
  template: string
  /** El texto final. En una automática, el que genera el servidor ahora mismo. */
  body: string
  /** Se escribe sola con los datos del negocio; editarla la convierte en propia. */
  auto: boolean
  /** Si está enlazada en el sitio. Una con huecos existe, pero no se publica. */
  published: boolean
  /** Los datos del negocio que le faltan para poder publicarse. */
  missingFields: (keyof BusinessInfo)[]
  updatedAt: string | null
  /** Dónde se publica en el sitio del cliente. */
  path: string
  /**
   * El plazo de aviso que el servidor puede proponer, si puede.
   *
   * Sólo llega en «cancelaciones» y sólo cuando hay UN servicio reservable: con
   * dos de plazos distintos, cualquier número en el documento sería falso para
   * uno de los dos.
   */
  suggestedNoticeHours?: number
}

export function listProjectPolicies(token: string, projectId: string) {
  return request<ProjectPolicy[]>(`/api/projects/${projectId}/policies`, token)
}

/**
 * Guarda una. Texto vacío la retira, sin dejar una página en blanco publicada;
 * `{ auto: true }` la pone —o la devuelve— a la versión automática.
 */
export function saveProjectPolicy(
  token: string,
  projectId: string,
  key: ProjectPolicy['key'],
  change: { body: string } | { auto: true },
) {
  return request<ProjectPolicy>(
    `/api/projects/${projectId}/policies/${key}`,
    token,
    { method: 'PATCH', body: JSON.stringify(change) },
  ).then(changedAttention(projectId))
}

/**
 * Sólo los ajustes del sitio (y su idioma), sin el contenido del proyecto: para
 * las pantallas de ajustes que no necesitan nada más. `getProject` baja el sitio
 * entero (cientos de KB) y eso es egress de Supabase por leer un interruptor.
 */
export function getProjectSiteSettings(token: string, id: string) {
  return request<{ siteSettings: SiteSettings; language: string }>(`/api/projects/${id}/publish-settings`, token)
}

export async function getProject(token: string, id: string) {
  /* `plan` es el del DUEÑO del proyecto, no el de quien lo abre: es lo que
     decide los topes que se enseñan dentro. Ver la ruta en el worker. */
  return request<Project & { data?: { pages?: unknown[] }; siteSettings?: SiteSettings; plan?: BillingPlan; dataRevision?: string }>(`/api/projects/${id}`, token)
}

/** Persist favicons / Veltica badge toggle / site password. The password is hashed
 *  server-side; pass `password` to set one, or `passwordProtected: false` to clear. */
export function updateSiteSettings(
  token: string,
  id: string,
  patch: Partial<{
    faviconLightDataUrl: string
    faviconDarkDataUrl: string
    webclipDataUrl: string
    showVelticaBadge: boolean
    passwordProtected: boolean
    password: string
    seoTitle: string
    seoDescription: string
    indexable: boolean
    ogImageUrl: string
    pendingItems: 'soon' | 'hide'
    cart: 'on' | 'off'
    openTable: { rids: string[] } | null
    googleAnalytics: { measurementId: string } | null
    customerAccounts: 'on' | 'off' | 'auto'
  }>,
) {
  return request<SiteSettings>(`/api/projects/${id}/site-settings`, token, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
}

export function updateProject(
  token: string,
  id: string,
  patch: {
    name?: string
    slug?: string
    data?: unknown
    timezone?: string
    /**
     * La versión de `data` sobre la que se editó. Con ella el servidor rechaza
     * (409 `project.conflict`) un guardado si entretanto cambió en otro sitio.
     */
    baseDataRevision?: string
  }
) {
  return request<Project & { dataRevision?: string }>(`/api/projects/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(patch),
  })
}

/**
 * Borra un proyecto — con ventana de gracia.
 *
 * No destruye nada todavía: baja el sitio y arranca el plazo. Devuelve cuántos
 * días quedan para poder decirlo en pantalla en vez de darlo por sabido.
 */
export function deleteProject(token: string, id: string) {
  return request<{ deletedAt: string; graceDays: number }>(`/api/projects/${id}`, token, {
    method: 'DELETE',
  })
}

export type DeletedProject = {
  id: string
  name: string
  deletedAt: string
  /** Cuándo se destruye de verdad. Lo calcula el servidor, que es quien lo aplica. */
  purgeAt: string | null
}

/** Los que están en su ventana de gracia, para poder ofrecer recuperarlos. */
export function listDeletedProjects(token: string): Promise<DeletedProject[]> {
  return request('/api/projects/deleted', token)
}

/** Deshace el borrado. El proyecto vuelve entero, pero hay que volver a publicar. */
export function restoreProject(token: string, id: string): Promise<{ restored: boolean; needsPublish: boolean }> {
  return request(`/api/projects/${id}/restore`, token, { method: 'POST' })
}

// ─── Per-page endpoints ───────────────────────────────────────────────────────
// Pages live in their own rows so the editor/dashboard don't ship the whole
// project on every save. Metadata (this list) is cheap; content is fetched per page.

export type PageMeta = {
  /** El uuid de la fila. `pageId` es el identificador con el que trabaja el editor. */
  id: string
  pageId: string
  name: string
  slug: string
  isHome: boolean
  cmsDynamic: unknown
  sortOrder: number
  updatedAt: string
  thumbnailUrl: string | null
}

const pagePath = (projectId: string, pageId: string) =>
  `/api/projects/${projectId}/pages/${encodeURIComponent(pageId)}`

/** Lightweight page list (metadata + thumbnail, no elements). */
export function listPages(token: string, projectId: string) {
  return request<PageMeta[]>(`/api/projects/${projectId}/pages`, token)
}

/** One page with full content. */
/** Every page with full content — for publish/preview. */
export function getPagesFull<T = unknown>(token: string, projectId: string) {
  return request<T[]>(`/api/projects/${projectId}/pages/full`, token)
}

/** Upsert a single page (autosave hot path — small payload). `keepalive` lets the
 *  request finish even if the tab is closing right after an edit/delete. */
export function savePage(token: string, projectId: string, pageId: string, page: unknown) {
  return request<{ pageId: string; updatedAt: string }>(pagePath(projectId, pageId), token, {
    method: 'PUT',
    body: JSON.stringify({ page }),
    keepalive: true,
  })
}

export function createPage(token: string, projectId: string, page: unknown) {
  return request<{ pageId: string }>(`/api/projects/${projectId}/pages`, token, {
    method: 'POST',
    body: JSON.stringify({ page }),
  })
}

export function deletePage(token: string, projectId: string, pageId: string) {
  return request<{ pageId: string }>(pagePath(projectId, pageId), token, { method: 'DELETE', keepalive: true })
}

/** Persist page order (array of page ids, in the new order). */
export function reorderPages(token: string, projectId: string, order: string[]) {
  return request<{ ok: boolean }>(`/api/projects/${projectId}/pages/order`, token, {
    method: 'PUT',
    body: JSON.stringify({ order }),
    keepalive: true,
  })
}

/** Take a published project offline (back to draft): removes the live site. */
// ─── Custom domains (Cloudflare for SaaS) ─────────────────────────────────────

/**
 * `purchasing` es el rato entre el cobro y la confirmación del registrador, y
 * sólo lo tienen los dominios comprados aquí. Llega por la API como cualquier
 * otro, así que todo lo que mapee estados tiene que contemplarlo.
 */
/**
 * `disconnected` es propio de los comprados aquí: al soltarlos, la fila se
 * queda —es la prueba de la compra y su fecha de renovación— y el dominio pasa
 * a estar suyo pero sin sitio. Los traídos de fuera simplemente se borran.
 */
export type CustomDomainStatus = 'purchasing' | 'pending_dns' | 'verifying' | 'active' | 'error' | 'disconnected'

/** A DNS record the domain owner must add (CNAME target / validation TXT). */
export type DomainValidationRecord = { type: string; name: string; value: string }

/** Live DNS diagnosis for a pending domain (why it isn't active yet). */
export type DomainDiagnosis = {
  state: 'not_found' | 'wrong_target' | 'dns_ok'
  foundTarget?: string | undefined
  provider?: string | undefined
  nameservers?: string[] | undefined
}

export type CustomDomain = {
  id: string
  hostname: string
  status: CustomDomainStatus
  sslStatus: string | null
  /** True when this is the project's canonical domain (others 301 here). */
  isPrimary: boolean
  /** The hostname to CNAME the domain to (same for every domain). */
  cnameTarget: string
  /** `'porkbun'` = lo compró aquí, así que su DNS lo llevamos nosotros. Nulo = lo trajo de fuera. */
  registrar: string | null
  /** Cuándo vence el registro, ISO. Nulo en los que trajo de otro registrador. */
  expiresAt: string | null
  verification: { records?: DomainValidationRecord[]; errors?: string[] }
  /** Present only while pending — precise DNS state to guide the user. */
  diagnosis?: DomainDiagnosis
}

export type ProjectDomains = {
  cnameTarget: string
  domains: CustomDomain[]
  /**
   * Sin Cloudflare de verdad detrás.
   *
   * Un dominio conectado en este modo salta a «activo» sin resolver a nada. Se
   * dice en pantalla: callarlo es cómo alguien acaba creyendo que su sitio está
   * en línea.
   */
  mock: boolean
}

/** List connected domains (refreshes DNS/SSL status server-side). */
export function listProjectDomains(token: string, projectId: string) {
  return request<ProjectDomains>(`/api/projects/${projectId}/domains`, token)
}

/** Connect a custom domain — creates the Cloudflare Custom Hostname. */
export function addProjectDomain(token: string, projectId: string, hostname: string) {
  return request<CustomDomain>(`/api/projects/${projectId}/domains`, token, {
    method: 'POST',
    body: JSON.stringify({ hostname }),
  })
}

/** Disconnect a custom domain. */
/**
 * Vuelve a enganchar un dominio comprado aquí que estaba suelto.
 *
 * Sin asistente ni pasos de DNS: su DNS vive en nuestra cuenta del registrador
 * y lo apuntamos nosotros. Uno traído de fuera no puede usar esto — ahí el DNS
 * es de su proveedor y hay que pasar por el asistente.
 */
/** La solicitud del código de transferencia de un dominio. */
export type AuthCodeRequest = {
  status: 'pending' | 'sent' | 'cancelled'
  requestedAt: string
  resolvedAt: string | null
  /** Sólo con la solicitud resuelta y el código sin caducar. */
  code: string | null
  codeExpiresAt: string | null
}

/** Si ya se pidió el código de este dominio. `null` = nunca se pidió. */
export function getAuthCodeRequest(token: string, projectId: string, domainId: string) {
  return request<AuthCodeRequest | null>(`/api/projects/${projectId}/domains/${domainId}/auth-request`, token)
}

/** Lo pide el sistema y avisa a quien lo atiende. Ver `AUTH_REQUEST_INBOX`. */
export function requestAuthCode(token: string, projectId: string, domainId: string) {
  return request<AuthCodeRequest>(`/api/projects/${projectId}/domains/${domainId}/auth-request`, token, {
    method: 'POST',
  })
}

/** Un pedido a la IA que no se pudo cumplir, agrupado por frase. */
export type AdminAiUnmet = {
  kind: 'section' | 'element' | 'font' | 'style' | 'feature'
  what: string
  total: number
  people: number
  fromAi: number
  fromCode: number
  flows: string[]
  categories: string[]
  lastSeen: string
}

/** El gasto de IA resumido (admin). Ver `adminAiSpend.ts` en el worker. */
export type AdminAiSpend = {
  total: number
  byFeature: Array<{ feature: string; calls: number; cached: number; users: number; cost: number; avgCost: number; p90Cost: number; avgInput: number; avgOutput: number; p90Tokens: number; maxTokens: number; estimated: number }>
  byPlan: Array<{ plan: string; users: number; cost: number; avgCost: number; p90Cost: number; maxCost: number; avgCalls: number }>
  byDay: Array<{ day: string; cost: number; calls: number }>
  topUsers: Array<{ userId: string; email: string | null; name: string | null; plan: string; cost: number; calls: number }>
}

export function getAiSpend(token: string, days: number) {
  return request<AdminAiSpend>(`/api/admin/ai-spend?days=${days}`, token)
}

export function listAiUnmet(token: string, days: number) {
  return request<AdminAiUnmet[]>(`/api/admin/ai-unmet?days=${days}`, token)
}

/** Una sección de una generación: lo que se construyó, con su bloque. */
export type AdminAiGenerationSection = { category: string; headline: string; items: number; templateId: string; template: string }

/** Una generación con IA, entera (ver `ai_generations` en el worker). */
export type AdminAiGeneration = {
  id: string
  createdAt: string
  flow: 'project' | 'page'
  status: 'ok' | 'failed'
  error: string | null
  prompt: string
  language: string | null
  model: string | null
  businessName: string | null
  plan: {
    sectionsFromUser?: boolean
    industry?: string
    /** Las categorías que mandó la IA por página, antes de que el código toque nada. */
    planned?: string[][]
    built?: { name: string; sections: AdminAiGenerationSection[] }[]
    extraSections?: number
    itemsRequested?: number
    trim?: { pages: number; sections: number }
    usage?: {
      inputTokens: number
      outputTokens: number
      thoughtTokens: number
      cachedInputTokens: number
      totalTokens: number
      calls: number
      estimated: boolean
    }
    cost?: {
      currency: 'USD'
      inputUsd: number
      outputUsd: number
      totalUsd: number
      rates: { inputPerMillion: number; cachedInputPerMillion: number; outputPerMillion: number; checkedAt: string }
    } | null
  }
  unmet: { source: 'ai' | 'code'; kind: string; what: string; category?: string }[]
  tokens: number | null
  costRange?: { currency: 'USD'; minUsd: number; maxUsd: number } | null
  durationMs: number | null
  userEmail: string | null
  project: { id: string; name: string | null; subdomain: string | null } | null
}

/** De 40 en 40, la más reciente primero; `before` sigue desde la última vista. */
export function listAiGenerations(token: string, days: number, before?: string) {
  return request<AdminAiGeneration[]>(`/api/admin/ai-unmet/generations?days=${days}${before ? `&before=${encodeURIComponent(before)}` : ''}`, token)
}

/** Una solicitud vista desde administración. */
export type AdminAuthCodeRequest = {
  id: string
  hostname: string
  status: string
  requestedAt: string
  resolvedAt: string | null
  note: string
  email: string | null
  projectName: string | null
}

export function listAuthCodeRequests(token: string, status: 'pending' | 'sent' | 'all' = 'pending') {
  return request<AdminAuthCodeRequest[]>(`/api/admin/auth-codes?status=${status}`, token)
}

/**
 * Entrega el código: lo guarda, avisa por correo y lo deja en el panel del
 * cliente. Ver `POST /admin/auth-codes/:id/code`.
 */
export function deliverAuthCode(token: string, id: string, code: string, note?: string) {
  return request<{ id: string; status: string }>(`/api/admin/auth-codes/${id}/code`, token, {
    method: 'POST',
    body: JSON.stringify({ code, ...(note ? { note } : {}) }),
  })
}

/** Códigos entregados que el cliente todavía no ha visto. Enciende la campana. */
export type ReadyAuthCode = { id: string; hostname: string; domainId: string; resolvedAt: string | null }

export function listReadyAuthCodes(token: string) {
  return request<ReadyAuthCode[]>('/api/account/auth-codes', token)
}

/** Un dominio de la cuenta, con el sitio en el que está. Ver `/account/domains`. */
export type AccountDomain = {
  id: string
  hostname: string
  status: string
  /** El sitio en el que está — o el último, si está suelto. */
  projectId: string | null
  projectName: string | null
  expiresAt: string | null
  sslStatus: string | null
  isPrimary: boolean
  priceCents: number | null
  currency: string
  purchasedAt: string
  /**
   * En qué punto está su código de transferencia.
   *
   * `none` si nadie lo pidió · `pending` mientras se prepara · `ready` cuando
   * está listo. El código en sí NO viaja aquí: se lee abriendo la ficha del
   * dominio, que es la única ruta que lo devuelve.
   */
  authCode: 'none' | 'pending' | 'ready'
  /** Si la renovación la cobramos nosotros con su tarjeta guardada. */
  autoRenew: boolean
  /** Lo que costó la última renovación conocida. Para enseñar una cifra, no para cobrar. */
  renewalPriceCents: number | null
  /** Por qué falló el último cobro automático, si falló. */
  renewalLastError: string | null
  /** Bloqueado mientras se revisa un reembolso o una disputa. */
  transferLocked: boolean
}

/** Todos los dominios comprados por la cuenta, con su sitio. */
export function listAccountDomains(token: string) {
  return request<AccountDomain[]>('/api/account/domains', token)
}

/** Enciende o apaga la renovación automática de un dominio. */
export function setDomainAutoRenew(token: string, domainId: string, enabled: boolean) {
  return request<{ autoRenew: boolean }>(`/api/account/domains/${domainId}/auto-renew`, token, {
    method: 'PATCH',
    body: JSON.stringify({ enabled }),
  })
}

/**
 * Abre el pago de una renovación y devuelve el secreto del checkout.
 *
 * El mismo `clientSecret` que la compra, así que lo pinta el mismo componente
 * embebido de Stripe.
 */
export function startDomainRenewal(token: string, domainId: string) {
  return request<{ clientSecret: string; domain: string; priceCents: number }>(
    `/api/account/domains/${domainId}/renew`,
    token,
    { method: 'POST' },
  )
}

/** Un dominio pagado y sin conectar a ningún sitio. Ver `/domains/available`. */
export type AvailableDomain = { id: string; hostname: string; expiresAt: string | null }

/**
 * Los dominios que la cuenta ya pagó y no está usando.
 *
 * Se piden desde un proyecto porque el permiso es suyo —`domains.manage`—, pero
 * la lista es de la CUENTA: son los que se pueden conectar aquí sin comprar nada.
 */
export function listAvailableDomains(token: string, projectId: string) {
  return request<AvailableDomain[]>(`/api/projects/${projectId}/domains/available`, token)
}

export function reconnectProjectDomain(token: string, projectId: string, domainId: string) {
  return request<CustomDomain>(`/api/projects/${projectId}/domains/${domainId}/reconnect`, token, { method: 'POST' })
}

export function deleteProjectDomain(token: string, projectId: string, domainId: string) {
  return request<{ id: string }>(`/api/projects/${projectId}/domains/${domainId}`, token, {
    method: 'DELETE',
  })
}

/** Un dominio que se puede comprar, tal como lo pinta el buscador. */
export type DomainOption = {
  domain: string
  tld: string
  /** El destacado arriba. Sale también en la rejilla, marcado. */
  selected: boolean
  /**
   * `null` cuando no se pudo comprobar, que no es lo mismo que «tomado».
   *
   * Pasa con las extensiones cuyo registro no publica RDAP y cuando el registro
   * no contesta. Se muestra aparte de las libres y de las ocupadas, porque
   * decir «disponible» sin saberlo acaba en una compra que falla.
   */
  available: boolean | null
  /** Los premium no se pueden comprar desde aquí aunque estén libres. */
  premium: boolean
  /**
   * Lo que se paga el PRIMER año con el crédito que se tiene ahora mismo.
   *
   * Igual a `priceCents` para quien no tiene crédito. `null` si el dominio no
   * está en venta. Lo calcula el servidor: depende del catálogo del día y del
   * estado de la suscripción, y aquí no está ninguno de los dos.
   */
  firstYearCents: number | null
  /** Cuánto crédito se aplica. Cero cuando no hay ninguno que aplicar. */
  creditAppliedCents: number
  /**
   * Lo que costaría el primer año CON el plan anual.
   *
   * Es el número que hace la oferta a quien todavía no lo tiene. Va aparte de
   * `firstYearCents` justamente para no confundir «lo que pagas» con «lo que
   * pagarías»: mezclarlos es la confusión que este modelo viene a evitar.
   */
  firstYearWithAnnualCents: number | null
  /**
   * Lo que paga el cliente al año, en centavos de dólar.
   *
   * `null` en un dominio ya registrado: no está en venta, y ponerle precio
   * invita a intentar comprar algo que no se puede comprar.
   */
  priceCents: number | null
  renewalCents: number | null
}

export type DomainSearch = {
  /** El nombre ya limpio, sin extensión. */
  term: string
  /** `mock` y `sandbox` no compran nada de verdad — el panel debe decirlo. */
  mode: 'mock' | 'sandbox' | 'live'
  /**
   * El que se destaca arriba: la extensión elegida a mano, o el `.com` si está
   * libre y, cuando no lo está, la primera libre.
   */
  selected: DomainOption | null
  /** Por qué no pudimos consultar el precio, si es que no pudimos. */
  unavailableReason: string | null
  /** Todas las extensiones ofrecidas, **incluida la destacada**. */
  options: DomainOption[]
  /** El crédito de dominio de quien mira, para saber qué ofrecerle. */
  credit: {
    /** Cuánto vale un crédito, se tenga o no. `null` si no se pudo saber. */
    valueCents: number | null
    /** Si lo tiene disponible ahora mismo. */
    available: boolean
    /** `none` sin plan anual, `spent` si ya lo usó, `null` si lo tiene. */
    reason: 'none' | 'spent' | null
  }
}

/** Busca un dominio para comprar. El término va sin extensión ("miempresa"). */
export function searchProjectDomains(token: string, projectId: string, query: string) {
  return request<DomainSearch>(`/api/projects/${projectId}/domains/search?q=${encodeURIComponent(query)}`, token)
}

/**
 * Abre el pago de un dominio.
 *
 * No registra nada: devuelve el `clientSecret` del formulario de Stripe. El
 * dominio se registra cuando Stripe confirma el cobro, no antes — registrar
 * primero nos dejaría pagando el dominio de quien abandone la pantalla de pago.
 */
export function purchaseDomain(token: string, projectId: string, domain: string) {
  return request<{
    /** `null` cuando el crédito lo cubre entero: no hay nada que cobrar. */
    clientSecret: string | null
    domain: string
    priceCents: number
    firstYearCents: number
    creditAppliedCents: number
    /**
     * El dominio se compró, pero algo falló al terminar de conectarlo.
     *
     * NO es «no se compró»: la fila ya existe y el dominio suele estar
     * registrado a nombre del cliente. Decirle que falló le empuja a comprar
     * otro, que es la equivocación cara de este flujo.
     */
    incomplete?: boolean
  }>(
    `/api/projects/${projectId}/domains/purchase`,
    token,
    { method: 'POST', body: JSON.stringify({ domain }) },
  )
}

/** Set the project's canonical domain (others 301 here). `null` = veltica subdomain. */
export function setPrimaryDomain(token: string, projectId: string, domainId: string | null) {
  return request<{ primaryDomainId: string | null }>(`/api/projects/${projectId}/domains/primary`, token, {
    method: 'PUT',
    body: JSON.stringify({ domainId }),
  })
}

/** Lo que el asistente necesita de cada sección de la portada para cambiarla de variante sin IA. */
export type AiDraftSection = {
  rootIds: string[]
  category: string
  templateId: string
  thumbnailUrl?: string | null
  copy: SectionCopy
  /** La familia de ofertas que enseña una tienda, y si el blog enseña las entradas creadas. */
  offers?: string
  seededBlog?: boolean
  alternatives: { id: string; name: string; thumbnailUrl: string | null }[]
  /**
   * Todas las variantes en un orden fijo, la original primero. Se fija al
   * primer cambio: así cada una conserva su número y se puede volver a la que
   * gustó. Sin él (nunca se cambió), es la actual seguida de `alternatives`.
   */
  variants?: { id: string; name: string; thumbnailUrl: string | null }[]
  /** Las variantes ya vistas, tal como quedaron, por id: volver a una la deja igual. */
  visited?: Record<string, unknown[]>
}

/** AI fields used to hydrate the create form (loose — the preview module owns strong types). */
export type AiDraft = {
  /** Si esta generación ya se descontó al generarla (pasó de las pruebas gratis). */
  charged?: boolean
  /** Cuántas pruebas gratis sin crear le quedan (30 días). */
  freeTriesLeft?: number
  aiProject: {
    projectName: string
    business: { name: string; tagline: string; description: string }
    style?: StyleRecipe
    features?: string[]
  }
  name: string
  project: {
    pages: Array<Record<string, unknown>>
    projectTheme: Record<string, unknown>
    projectContent?: Record<string, unknown>
    aiGeneration?: {
      language?: string
      /** Los datos de contacto que dio el cliente en su pedido. */
      contacts?: { kind: string; value: string }[]
      /** El horario que dio el cliente en su pedido. */
      hours?: { days: string; time: string }[]
      locations?: { name: string; address: string; phone?: string; hours?: string }[]
      portrait?: string | null
      exampleContent?: boolean
      features?: string[]
      trimmed?: { pages: number; sections: number }
    }
  }
  sections?: AiDraftSection[]
  /** Lo mismo de cada página, en el orden de `project.pages` (la 0 es la portada). */
  pageSections?: AiDraftSection[][]
}

/** Dry-run AI generation: returns the structured draft + composed project WITHOUT persisting. */
/**
 * `language`: el del panel. La IA escribe en el idioma de la descripción; éste
 * sólo decide cuando no se nota.
 */
/**
 * `brief`: las respuestas de bienvenida (qué negocio, qué debe hacer el sitio).
 * Van aparte del prompt: son contexto para la IA, no palabras de quien escribe.
 */
export function draftProjectWithAi(
  token: string,
  prompt: string,
  language?: 'es' | 'en',
  brief?: { business?: string; goals?: string[] },
  /** El look elegido antes de generar (un estilo de diseño). Sin él, elige la IA. */
  lookId?: string | null,
  /** Las fotos propias que subió en el asistente (`wizardPhotos`), en su orden. */
  photoIds?: string[],
) {
  return request<AiDraft>('/api/ai/draft-project', token, {
    method: 'POST',
    body: JSON.stringify({ prompt, ...(language ? { language } : {}), ...(brief ? { brief } : {}), ...(lookId ? { lookId } : {}), ...(photoIds?.length ? { photoIds } : {}) }),
  })
}

export type UploadedAsset = {
  id: string
  url: string
  filename: string
  sizeBytes: number
  mimeType: string
  createdAt: string
}

/** Imágenes que el usuario ya subió, para reutilizarlas sin duplicar archivos. */
export function listUploadedAssets(token: string, limit = 100, offset = 0) {
  return request<UploadedAsset[]>(`/api/assets?limit=${limit}&offset=${offset}`, token)
}

/** Upload an image to the user's asset bucket. Returns the public URL. */
/**
 * Sube una imagen. `projectId` decide dónde vive el archivo.
 *
 * Con proyecto, el objeto se guarda bajo su prefijo y acompaña al sitio si
 * algún día cambia de dueño. Sin él queda en la biblioteca de la cuenta, que no
 * viaja con ningún proyecto — que es lo correcto para un logo o una firma que
 * se reutiliza en varios.
 */
export async function uploadAsset(
  token: string,
  file: File,
  projectId?: string,
  options: {
    /** El tamaño de la imagen, si ya se midió: con él la IA sabe dónde cabe. */
    size?: { width: number; height: number } | undefined
    /** Que la IA la mire al llegar: las fotos del asistente de sitio nuevo (`wizardPhotos`). */
    describe?: boolean
  } = {},
): Promise<{ url: string; id?: string }> {
  const { size, describe } = options
  // Las copias se codifican mientras el máster sube (ver `prepareImageUpload`).
  const prepared = await prepareImageUpload(file)
  const uploadFile = prepared.file
  const form = new FormData()
  form.append('file', uploadFile)
  if (projectId) form.append('projectId', projectId)
  if (size) { form.append('width', String(size.width)); form.append('height', String(size.height)) }
  if (describe) form.append('describe', '1')
  const send = (t: string) => fetch(`${WORKER_URL}/api/assets/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${t}` },
    body: form,
  })

  let authToken = (await freshToken(token)) ?? token
  let res = await send(authToken)
  if (res.status === 401) {
    const refreshed = await forceRefresh()
    // Se guarda el que acabó valiendo: las copias estrechas viajan después, y
    // con el caducado las rechazaría el guardia una por una.
    if (refreshed) {
      authToken = refreshed
      res = await send(refreshed)
    }
  }
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new Error(`${res.status}: ${body}`)
  }
  const json = (await res.json()) as { data: { url: string; id?: string } }

  /**
   * Las copias estrechas, igual que en el editor.
   *
   * Sin esto, todo lo que entra por el panel —las imágenes del CMS, las
   * portadas, la biblioteca— se publicaba con una sola resolución: el móvil
   * bajaba el archivo grande. La compresión ya estaba aquí; faltaba la mitad
   * que arregla la resolución, que en una página real pesa más.
   *
   * A la vez entre ellas, y en silencio si alguna falla: es un extra, y hacer
   * esperar a quien sube la imagen por un extra sería peor negocio que
   * publicarla con un `srcset` más corto.
   */
  const variants = await prepared.variants
  await Promise.all(variants.map(async (variant) => {
    const variantForm = new FormData()
    variantForm.append('file', variant.file)
    // La variante cuelga de la clave del original, así que declara el mismo
    // proyecto o el guardia la rechaza.
    if (projectId) variantForm.append('projectId', projectId)
    variantForm.append('variantOf', json.data.url)
    variantForm.append('variantWidth', String(variant.width))
    await uploadFormDataWithProgress({
      url: `${WORKER_URL}/api/assets/upload`,
      formData: variantForm,
      headers: { Authorization: `Bearer ${authToken}` },
    }).catch(() => null)
  }))

  return json.data
}

/** El tope de la lista del admin: el mismo que acepta el servidor. */
export const ADMIN_TEMPLATES_LIMIT = 200

/** Los templates del admin, con cuántos hay en total: si pasan del tope, la lista no los trae todos. */
export async function listAdminTemplates(token: string): Promise<{ data: AdminTemplate[]; total: number }> {
  /* Esta ruta da el total en `meta`, no al lado de `data` como `Paged`. Da
     como mucho `ADMIN_TEMPLATES_LIMIT` por página: se piden las que hagan falta
     hasta tenerlos todos (con un tope, por si el total cambiara a medio camino). */
  const data: AdminTemplate[] = []
  let total = 0
  for (let page = 1; page <= 25; page += 1) {
    const res = await requestPage<AdminTemplate>(`/api/admin/templates?limit=${ADMIN_TEMPLATES_LIMIT}&page=${page}`, token) as unknown as { data: AdminTemplate[]; meta?: { total?: number } }
    total = res.meta?.total ?? total
    data.push(...res.data)
    if (res.data.length < ADMIN_TEMPLATES_LIMIT || data.length >= total) break
  }
  /* Un template que cambió de página entre una petición y otra saldría dos veces. */
  const unique = [...new Map(data.map((item) => [item.id, item])).values()]
  return { data: unique, total: Math.max(total, unique.length) }
}

/** El lienzo del borrador de un template, para ver si está listo para la IA. */
export async function getAdminTemplateDraftElements(token: string, id: string): Promise<unknown[]> {
  const draft = await request<{ data?: { elements?: unknown } | null }>(`/api/admin/templates/${id}/draft`, token)
  const elements = draft.data?.elements
  return Array.isArray(elements) ? (elements as unknown[]) : []
}

export function createAdminTemplate(token: string, payload: AdminTemplatePayload) {
  return request<AdminTemplate>('/api/admin/templates', token, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export function updateAdminTemplateMeta(token: string, id: string, patch: Partial<AdminTemplatePayload>) {
  return request<AdminTemplate>(`/api/admin/templates/${id}/meta`, token, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
}

export function publishAdminTemplate(token: string, id: string) {
  return request<AdminTemplate>(`/api/admin/templates/${id}/publish`, token, {
    method: 'POST',
  })
}

export function archiveAdminTemplate(token: string, id: string) {
  return request<AdminTemplate>(`/api/admin/templates/${id}/archive`, token, {
    method: 'POST',
  })
}

export function activateAdminTemplate(token: string, id: string) {
  return request<AdminTemplate>(`/api/admin/templates/${id}/activate`, token, {
    method: 'POST',
  })
}

export function resetAdminTemplateDraft(token: string, id: string) {
  return request<AdminTemplate>(`/api/admin/templates/${id}/reset-draft`, token, {
    method: 'POST',
  })
}

export function deleteAdminTemplate(token: string, id: string) {
  return request<{ id: string }>(`/api/admin/templates/${id}`, token, {
    method: 'DELETE',
  })
}

export async function listProfessionalImagesPage(
  token: string,
  options: { query?: string; cursor?: string | null; limit?: number; media?: 'image' | 'video' | 'all' } = {},
) {
  const params = new URLSearchParams({ limit: String(options.limit ?? 48) })
  if (options.media) params.set('media', options.media)
  if (options.query) params.set('q', options.query)
  if (options.cursor) params.set('cursor', options.cursor)
  let authToken = (await freshToken(token)) ?? token
  let response = await doFetch(`/api/image-library?${params}`, authToken, undefined, IMAGE_LIBRARY_WORKER_URL)
  if (response.status === 401) {
    const refreshed = await forceRefresh()
    if (refreshed) {
      authToken = refreshed
      response = await doFetch(`/api/image-library?${params}`, authToken, undefined, IMAGE_LIBRARY_WORKER_URL)
    }
  }
  if (!response.ok) throw new Error(`${response.status}: ${await response.text().catch(() => '')}`)
  return response.json() as Promise<{ data: ProfessionalImage[]; meta: { nextCursor: string | null } }>
}

export async function uploadProfessionalImage(
  token: string,
  input: {
    file: File
    title: string
    hashtags: string[]
    altText?: string
    onProgress?: (percent: number) => void
  },
) {
  const uploadFile = await compressImageForUpload(input.file)
  const form = new FormData()
  form.append('file', uploadFile)
  form.append('title', input.title)
  form.append('hashtags', input.hashtags.join(','))
  if (input.altText) form.append('altText', input.altText)
  const send = (authToken: string) => uploadFormDataWithProgress({
    url: `${IMAGE_LIBRARY_WORKER_URL}/api/admin/image-library/upload`,
    formData: form,
    headers: { Authorization: `Bearer ${authToken}` },
    ...(input.onProgress ? { onProgress: input.onProgress } : {}),
  })
  const authToken = (await freshToken(token)) ?? token
  let response = await send(authToken)
  if (response.status === 401) {
    const refreshed = await forceRefresh()
    if (refreshed) response = await send(refreshed)
  }
  if (response.status < 200 || response.status >= 300) {
    throw new Error(`${response.status}: ${response.responseText}`)
  }
  return (JSON.parse(response.responseText) as { data: ProfessionalImage }).data
}

/** Lo que admite la galería en vídeo: lo que reproducen todos los navegadores. */
export const LIBRARY_VIDEO_TYPES = ['video/mp4', 'video/webm']
export const LIBRARY_VIDEO_MAX_BYTES = 95 * 1024 * 1024

/**
 * Un vídeo para la galería, como admin. Como los vídeos propios del editor:
 * el MP4 se reordena para que arranque sin bajarlo entero, va en el cuerpo de
 * un PUT (el worker lo pasa a R2 por partes) y después se sube su portada, un
 * cuadro del propio vídeo.
 */
export async function uploadProfessionalVideo(
  token: string,
  input: { file: File; title: string; hashtags: string[]; onProgress?: (percent: number) => void },
) {
  const { file } = input
  const poster = await videoPosterFile(file, 'cover').catch(() => null)
  const body: Blob = file.type === 'video/mp4'
    ? await file.arrayBuffer().then((buffer) => faststartMp4(buffer)).then((fast) => (fast ? new Blob([fast], { type: file.type }) : file)).catch(() => file)
    : file
  const query = new URLSearchParams({ name: file.name, title: input.title, hashtags: input.hashtags.join(',') })
  const send = (authToken: string) => new Promise<{ status: number; text: string }>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', `${IMAGE_LIBRARY_WORKER_URL}/api/admin/image-library/video?${query}`)
    xhr.setRequestHeader('Authorization', `Bearer ${authToken}`)
    xhr.setRequestHeader('Content-Type', file.type)
    xhr.upload.onprogress = (event) => { if (event.lengthComputable) input.onProgress?.(Math.round((event.loaded / event.total) * 100)) }
    xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText })
    xhr.onerror = () => reject(new Error('network'))
    xhr.send(body)
  })
  let authToken = (await freshToken(token)) ?? token
  let response = await send(authToken)
  if (response.status === 401) {
    const refreshed = await forceRefresh()
    if (refreshed) { authToken = refreshed; response = await send(refreshed) }
  }
  if (response.status !== 201) throw new Error(`${response.status}: ${response.text}`)
  let video = (JSON.parse(response.text) as { data: ProfessionalImage }).data
  if (poster) {
    const posted = await fetch(`${IMAGE_LIBRARY_WORKER_URL}/api/admin/image-library/${video.id}/poster`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'image/jpeg' },
      body: poster,
    }).catch(() => null)
    if (posted?.ok) video = ((await posted.json()) as { data: ProfessionalImage }).data
  }
  return video
}

export function updateProfessionalImage(
  token: string,
  id: string,
  patch: { title?: string; hashtags?: string[]; altText?: string },
) {
  return request<ProfessionalImage>(`/api/admin/image-library/${id}`, token, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  }, IMAGE_LIBRARY_WORKER_URL)
}

export function deleteProfessionalImage(token: string, id: string) {
  return request<void>(`/api/admin/image-library/${id}`, token, { method: 'DELETE' }, IMAGE_LIBRARY_WORKER_URL)
}

// ─── Billing ──────────────────────────────────────────────────────────────────

export type BillingPlan = 'free' | 'pro' | 'business'
export type BillingCycle = 'monthly' | 'yearly'

export type BillingState = {
  plan: BillingPlan
  status: string
  currentPeriodStart: string | null
  currentPeriodEnd: string | null
}

/** Current plan/status for the signed-in user (drives the plans UI). */
export function getBilling(token: string) {
  return request<BillingState>('/api/billing/me', token)
}

// Cached so the plan badge and the Plans page paint the correct plan instantly
// instead of flashing "free" for the duration of the /me round-trip. The memo
// is shared across components; localStorage survives reloads.
const BILLING_LS_KEY = 'veltica:billing-plan'
let billingMemo: BillingState | null = null

/** Last known plan — reads memory, then localStorage, then defaults to free. */
export function cachedBillingPlan(): BillingPlan {
  if (billingMemo) return billingMemo.plan
  try {
    const v = localStorage.getItem(BILLING_LS_KEY)
    if (v === 'free' || v === 'pro' || v === 'business') return v
  } catch { /* localStorage unavailable — fall through */ }
  return 'free'
}

/** Fetch billing and update the shared cache. Use instead of getBilling in UI. */
export async function refreshBilling(token: string): Promise<BillingState> {
  const b = await getBilling(token)
  billingMemo = b
  try { localStorage.setItem(BILLING_LS_KEY, b.plan) } catch { /* ignore */ }
  return b
}

/**
 * Starts a Stripe Checkout Session and returns the secret that mounts it.
 *
 * Embedded rather than hosted: the caller renders the form in place instead of
 * sending the customer to a Stripe-branded page mid-purchase.
 */
export function startCheckout(token: string, plan: 'pro' | 'business', cycle: BillingCycle) {
  return request<{ clientSecret: string }>('/api/billing/checkout', token, {
    method: 'POST',
    body: JSON.stringify({ plan, cycle }),
  })
}

/**
 * Cierra el pago de membresía que quedó abierto. Se llama al cerrar el
 * formulario de pago, para que empezar de nuevo no espere a que caduque solo.
 */
export function cancelCheckout(token: string) {
  return request<{ released: boolean }>('/api/billing/checkout/cancel', token, { method: 'POST' })
}

/** Opens the Stripe Customer Portal (manage/cancel/change card). */
export function openBillingPortal(token: string) {
  return request<{ url: string }>('/api/billing/portal', token, { method: 'POST' })
}

/* ── Facturación ───────────────────────────────────────────────────────────
   Lo que de verdad se le cobra a un usuario son dos cosas: la membresía —una
   suscripción— y los dominios —pagos anuales sueltos—. Los planes de workspace
   no existen todavía, así que aquí no hay nada de eso. */

export type BillingMembership = {
  plan: BillingPlan
  /** Estado tal cual lo dice Stripe (`active`, `past_due`…), o `none`. */
  status: string
  interval: 'month' | 'year' | null
  /** Precio de lista del plan, por periodo. */
  amountCents: number | null
  currency: string
  /** Cuándo se renueva, o cuándo termina si está marcada para cancelarse. */
  renewsAt: string | null
  cancelAtPeriodEnd: boolean
  /** Lo que Stripe cobrará la próxima vez, ya con descuentos e impuestos. */
  nextChargeCents: number | null
  nextChargeAt: string | null
  /**
   * Si detrás del plan hay un cobro real. Falso cuando el plan está guardado
   * pero Stripe no conoce la suscripción —otorgado a mano, o id huérfano—: no
   * hay importe ni fechas de cobro, y no hay nada que cancelar.
   */
  billable: boolean
}

export type BillingDomainCharge = {
  id: string
  hostname: string
  projectId: string
  projectName: string
  status: string
  pricePaidCents: number | null
  renewalPriceCents: number | null
  currency: string
  purchasedAt: string
  renewsAt: string | null
  /** El certificado. Nulo mientras Cloudflare no ha dicho nada todavía. */
  sslStatus: string | null
}

/** Los tipos de registro que el panel deja crear. Ver `EDITABLE_DNS_TYPES`. */
export type DnsRecordType = 'A' | 'AAAA' | 'CNAME' | 'MX' | 'TXT'

export type DnsRecord = {
  id: string
  /** El nombre completo, que es el que se reconoce en la tabla. */
  name: string
  /** El subdominio suelto, que es lo que edita el formulario. */
  shortName: string
  type: string
  content: string
  ttl: number
  prio: number | null
  /**
   * Los que mantienen el sitio en línea y su certificado.
   *
   * Se enseñan pero no se tocan. El servidor lo comprueba otra vez —ver
   * `isProtected` en `routes/domains.ts`—: un candado que sólo vive aquí lo
   * abre cualquiera con la consola del navegador.
   */
  locked: boolean
}

export type DnsInput = {
  type: DnsRecordType
  /** Vacío = la raíz. Sin el dominio detrás. */
  name: string
  content: string
  ttl?: number
  prio?: number
}

export function listDnsRecords(projectId: string, domainId: string, token: string) {
  return request<{ hostname: string; records: DnsRecord[]; editableTypes: DnsRecordType[] }>(
    `/api/projects/${projectId}/domains/${domainId}/dns`,
    token,
  )
}

export function createDnsRecord(projectId: string, domainId: string, record: DnsInput, token: string) {
  return request<{ ok: true }>(`/api/projects/${projectId}/domains/${domainId}/dns`, token, {
    method: 'POST',
    body: JSON.stringify(record),
  })
}

export function updateDnsRecord(projectId: string, domainId: string, recordId: string, record: DnsInput, token: string) {
  return request<{ ok: true }>(`/api/projects/${projectId}/domains/${domainId}/dns/${recordId}`, token, {
    method: 'PATCH',
    body: JSON.stringify(record),
  })
}

export function deleteDnsRecord(projectId: string, domainId: string, recordId: string, token: string) {
  return request<{ ok: true }>(`/api/projects/${projectId}/domains/${domainId}/dns/${recordId}`, token, {
    method: 'DELETE',
  })
}

export type BillingCard = {
  id: string
  brand: string
  last4: string
  expMonth: number
  expYear: number
  isDefault: boolean
}

export type BillingInvoice = {
  id: string
  number: string | null
  createdAt: string
  amountCents: number
  currency: string
  status: string
  description: string
  hostedUrl: string | null
  pdfUrl: string | null
}

export type BillingOverview = {
  membership: BillingMembership
  domains: BillingDomainCharge[]
  cards: BillingCard[]
  /** La primera página del historial; las siguientes con `listBillingInvoices`. */
  invoices: BillingInvoice[]
  invoicesHasMore: boolean
  invoicesCursor: string | null
  /** Falso mientras el usuario no haya pagado nada: no hay cliente en Stripe. */
  hasCustomer: boolean
}

/**
 * Una página del historial de facturación.
 *
 * Stripe pagina por cursor: no hay número de página ni total, sólo «lo que
 * viene después de esta factura». `nextCursor` es null cuando ya no hay más.
 */
export type BillingInvoicePage = {
  invoices: BillingInvoice[]
  hasMore: boolean
  nextCursor: string | null
}

/**
 * Una copia de seguridad de un proyecto, tal y como la lista el panel.
 *
 * Sin el contenido: eso se pide aparte y sólo al abrir la vista previa, porque
 * son cientos de KB por copia y el listado enseña treinta.
 */
export type ProjectBackup = {
  id: string
  kind: 'publish' | 'manual' | 'pre_restore'
  label: string
  sizeBytes: number
  pageCount: number
  collectionCount: number
  cmsItemCount: number
  createdAt: string
  /**
   * Quién la hizo.
   *
   * `null` en las anteriores a que se guardara el autor, y también si Auth no
   * contestó. La pantalla lo dice en vez de atribuirlas: dar por hecho que fue
   * el dueño sería inventarse un dato en la única lista donde importa quién
   * tocó qué.
   */
  author: { name: string; email: string; avatarUrl: string | null } | null
}

export type ProjectBackupList = {
  backups: ProjectBackup[]
  plan: BillingPlan
  /** Cuántas guarda el plan. El listado nunca trae más que esto. */
  limit: number
}

export function listProjectBackups(projectId: string, token: string) {
  return request<ProjectBackupList>(`/api/projects/${projectId}/backups`, token)
}

export function createProjectBackup(projectId: string, token: string, label?: string) {
  return request<{ id: string; sizeBytes: number }>(`/api/projects/${projectId}/backups`, token, {
    method: 'POST',
    body: JSON.stringify(label ? { label } : {}),
  })
}

export function deleteProjectBackup(projectId: string, backupId: string, token: string) {
  return request<{ id: string }>(`/api/projects/${projectId}/backups/${backupId}`, token, { method: 'DELETE' })
}

/**
 * Lo que quedaría por encima del plan gratis si la membresía terminara hoy.
 *
 * Se pide al abrir el diálogo de cancelar: es la única ventana en la que aún se
 * puede decidir con las cifras propias delante.
 */
export type DowngradePreview = {
  projects: Array<{
    projectId: string
    projectName: string
    overages: Array<{ resource: LimitedResource; used: number; limit: number; over: number }>
  }>
}

export function getDowngradePreview(token: string) {
  return request<DowngradePreview>('/api/billing/downgrade-preview', token)
}

/** La página siguiente del historial, desde el cursor de la anterior. */
export function listBillingInvoices(token: string, startingAfter?: string) {
  const query = startingAfter ? `?starting_after=${encodeURIComponent(startingAfter)}` : ''
  return request<BillingInvoicePage>(`/api/billing/invoices${query}`, token)
}

/** Membresía, dominios, tarjetas y facturas del usuario, en una sola llamada. */
export function getBillingOverview(token: string) {
  return request<BillingOverview>('/api/billing/overview', token)
}

/** Abre un SetupIntent para guardar una tarjeta nueva con el Payment Element. */
export function startCardSetup(token: string) {
  return request<{ clientSecret: string }>('/api/billing/payment-methods/setup', token, { method: 'POST' })
}

/** Deja esta tarjeta como la de los cobros futuros (cliente y suscripción). */
export function setDefaultCard(token: string, paymentMethodId: string) {
  return request<{ id: string }>(`/api/billing/payment-methods/${paymentMethodId}/default`, token, { method: 'POST' })
}

/** Quita una tarjeta guardada. El servidor niega la última con membresía viva. */
export function removeCard(token: string, paymentMethodId: string) {
  return request<{ id: string }>(`/api/billing/payment-methods/${paymentMethodId}`, token, { method: 'DELETE' })
}

/** Cancela la membresía al final del periodo ya pagado. */
export function cancelMembership(token: string) {
  return request<{ cancelAtPeriodEnd: boolean }>('/api/billing/subscription/cancel', token, { method: 'POST' })
}

/** Deshace una cancelación programada. */
export function resumeMembership(token: string) {
  return request<{ cancelAtPeriodEnd: boolean }>('/api/billing/subscription/resume', token, { method: 'POST' })
}

// ─── Admin: users & roles ─────────────────────────────────────────────────────

export type AdminUserRow = {
  id: string
  email: string | null
  createdAt: string
  /** Última entrada a la cuenta. Nula si nunca ha entrado. */
  lastSignInAt: string | null
  role: UserRole
  isSuperadmin: boolean
  plan: BillingPlan
  subscriptionStatus: string
  projectCount: number
}

export type AdminUserProject = {
  id: string
  name: string
  slug: string
  subdomain: string | null
  /** Captura del sitio, la misma que enseña la tarjeta del proyecto. */
  thumbnailUrl: string | null
  /** Dominio propio activo, si lo tiene: es la dirección de verdad del sitio. */
  primaryDomain: string | null
  publishedAt: string | null
  createdAt: string
  updatedAt: string
}

export type AdminUserDetail = {
  id: string
  email: string | null
  createdAt: string
  role: UserRole
  isSuperadmin: boolean
  subscription: {
    plan: BillingPlan
    status: string
    currentPeriodEnd: string | null
    stripeCustomerId: string | null
  }
  projects: AdminUserProject[]
}

export function listAdminUsers(token: string) {
  return request<AdminUserRow[]>('/api/admin/users', token)
}

export function getAdminUser(token: string, id: string) {
  return request<AdminUserDetail>(`/api/admin/users/${id}`, token)
}

export function updateUserRole(token: string, id: string, role: UserRole) {
  return request<{ userId: string; role: UserRole }>(`/api/admin/users/${id}/role`, token, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  })
}

// ─── Analytics ────────────────────────────────────────────────────────────────

/**
 * Dos familias, y la diferencia importa: `7d`/`30d`/… son ventanas móviles
 * contadas hacia atrás desde ayer; `lastWeek`/`lastMonth`/`lastYear` son
 * periodos naturales cerrados. "Último mes" y "últimos 30 días" casi nunca
 * cubren las mismas fechas.
 */
export type AnalyticsRange =
  | 'today' | 'yesterday'
  | '7d' | '30d' | '90d' | '12m'
  | 'lastWeek' | 'lastMonth' | 'lastYear'
  | 'custom'

/** Contra qué se mide el tramo elegido. */
export type AnalyticsCompare = 'previous' | 'year' | 'none'

export type AnalyticsQuery = {
  range: AnalyticsRange
  /** Sólo con `range: 'custom'`, en formato YYYY-MM-DD. */
  from?: string
  to?: string
  compare?: AnalyticsCompare
}

export type ProjectAnalytics = {
  range: AnalyticsRange
  /** El tramo que el servidor acabó usando, ya resuelto a fechas concretas. */
  window?: { from: string; to: string; days: number }
  compare?: AnalyticsCompare
  /**
   * El tramo contra el que se mide. `null` cuando se pidió no comparar.
   *
   * `series` viene alineada por posición con la principal —día 1 sobre día 1—
   * porque son fechas distintas y casarlas por fecha no solaparía nada.
   */
  comparison?: {
    from: string
    to: string
    series: { day: string; views: number; uniques: number }[]
    totals: { views: number; uniques: number }
  } | null
  /**
   * The zone these day buckets were counted in. Optional on purpose: the
   * dashboard and the worker deploy independently, so a field added to the API
   * is absent until the worker ships. Treat every new response field as
   * possibly-undefined rather than trusting the type.
   */
  timezone?: string
  totals: {
    views: number
    uniques: number
    /** Same metrics over the equally-long period before, for the trend badges. */
    viewsPrev: number
    uniquesPrev: number
  }
  /**
   * The chart's buckets. For most ranges one entry per day; for 'today'/
   * 'yesterday' it's 24 hourly points (`hour` 0–23, `uniques` always 0 since
   * uniques aren't tracked per hour). Gaps come back as 0.
   */
  series: { day: string; hour?: number; views: number; uniques: number }[]
  /**
   * Bucket granularity of `series`. Absent on workers that predate the intraday
   * chart — treat a missing value as 'day'.
   */
  granularity?: 'day' | 'hour'
  /** `exists: false` → no live page serves this URL anymore (deleted/renamed). */
  topPages: { path: string; views: number; exists?: boolean }[]
  topSources: { source: string; views: number }[]
  topCountries: { country: string; views: number }[]
  countriesCount: number
  /** Móvil / Tablet / Escritorio. Absent on workers that predate the breakdown. */
  devices?: { device: string; views: number }[]
  /**
   * utm_campaign values. Absent on older workers; empty for untagged sites.
   * Each campaign carries its own tag breakdown (source/medium/content/term/
   * device). Those arrays are absent on workers that predate the cross-tab, so
   * treat them as possibly-undefined.
   */
  topCampaigns?: {
    campaign: string
    views: number
    sources?: { value: string; views: number }[]
    mediums?: { value: string; views: number }[]
    contents?: { value: string; views: number }[]
    terms?: { value: string; views: number }[]
    devices?: { value: string; views: number }[]
  }[]
}

export function getProjectAnalytics(token: string, projectId: string, query: AnalyticsQuery) {
  const search = new URLSearchParams({ range: query.range })
  if (query.from) search.set('from', query.from)
  if (query.to) search.set('to', query.to)
  if (query.compare) search.set('compare', query.compare)
  return request<ProjectAnalytics>(`/api/projects/${projectId}/analytics?${search.toString()}`, token)
}

/** Clave estable de una consulta, para caché y para dependencias de efectos. */
export function analyticsQueryKey(query: AnalyticsQuery): string {
  return [query.range, query.from ?? '', query.to ?? '', query.compare ?? 'previous'].join('|')
}

/** Lo que se puede saber de UNA página. Sin únicos: no se cuentan por ruta. */
export type PageAnalytics = {
  page: { id: string; name: string; path: string; isHome: boolean; createdAt: string; updatedAt: string }
  timezone: string
  window: { from: string; to: string; days: number }
  compare: AnalyticsCompare
  comparison: { from: string; to: string } | null
  views: {
    series: { day: string; views: number; uniques: number }[]
    comparison: { day: string; views: number; uniques: number }[]
    total: number
    totalPrev: number
    /** Cuota sobre el tráfico del sitio en ese tramo, en tanto por uno. */
    shareOfSite: number
  }
  forms: {
    series: { day: string; total: number }[]
    comparison: { day: string; total: number }[]
    total: number
    totalPrev: number
  }
}

export function getPageAnalytics(token: string, projectId: string, pageId: string, query: AnalyticsQuery) {
  const search = new URLSearchParams({ range: query.range })
  if (query.from) search.set('from', query.from)
  if (query.to) search.set('to', query.to)
  if (query.compare) search.set('compare', query.compare)
  return request<PageAnalytics>(
    `/api/projects/${projectId}/pages/${encodeURIComponent(pageId)}/analytics?${search.toString()}`,
    token,
  )
}

// ── Formularios ─────────────────────────────────────────────────────────────

export type FormSubmission = {
  id: string
  formId: string
  formName: string
  /**
   * Las etiquetas del FORMULARIO, no del mensaje.
   *
   * Se ponen en el editor y valen para todos sus envíos, también los que
   * llegaron antes de ponerlas. Ausentes en las filas de antes de que esto
   * existiera, de ahí el opcional.
   */
  formTags?: string[] | null
  data: Record<string, string>
  name: string
  email: string
  /** Exact published page where the form was submitted; absent on old rows. */
  pageUrl?: string | null
  status: 'unread' | 'read' | 'spam'
  overLimit: boolean
  createdAt: string
}

export type ProjectForm = {
  id: string
  name: string
  notificationEmail: string
  successMessage: string
  status: string
}

export function listProjectForms(projectId: string, token: string) {
  return request<ProjectForm[]>(`/api/projects/${projectId}/forms`, token)
}

export type ContactStatus = 'active' | 'spam' | 'blocked'

export type ManualContact = {
  id: string
  projectId: string
  name: string
  email: string
  phone: string
  note: string
  tags: string[]
  status: ContactStatus
  createdAt: string
  updatedAt: string
}

/** Campos editables de una persona. Todo opcional: se parchea lo que cambió. */
export type ContactPatch = Partial<Pick<ManualContact, 'name' | 'phone' | 'note' | 'tags' | 'status'>>

/** Las etiquetas que ya llevan los contactos del proyecto, con cuántos las llevan. */
export function listContactTags(projectId: string, token: string) {
  return request<{ name: string; count: number }[]>(`/api/projects/${projectId}/contacts/tags`, token)
}

/** Quita una etiqueta de todos los contactos; devuelve a cuántos se la quitó. */
export function removeContactTag(projectId: string, name: string, token: string) {
  return request<{ name: string; contacts: number }>(`/api/projects/${projectId}/contacts/tags/remove`, token, {
    method: 'POST',
    body: JSON.stringify({ name }),
  })
}

export function updateContact(projectId: string, identity: string, patch: ContactPatch, token: string) {
  return request<ManualContact>(`/api/projects/${projectId}/contacts/${encodeURIComponent(identity)}`, token, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
}

/**
 * Una persona del proyecto, tal como la arma el servidor.
 *
 * No es una fila de `contacts`: es el resultado de agrupar sus envíos y montarle
 * encima la ficha editada a mano, si existe. `activities` sólo viene en el
 * detalle — arrastrar el historial de todos en la lista era justamente lo que
 * hacía que la respuesta creciera sin límite.
 */
export type ContactRecord = {
  id: string
  name: string
  email: string
  phone: string
  firstSeen: string
  lastSeen: string
  forms: string[]
  activityCount: number
  /** Cuántas veces ha comprado. Sale de sus cobros, no de una ficha guardada. */
  purchaseCount: number
  note: string
  tags: string[]
  status: ContactStatus
  initialSource: 'manual' | 'form' | 'purchase' | 'booking' | 'account'
  /** Si entró alguna vez a su cuenta del sitio (`/account`). Como en Shopify, un dato de la persona. */
  hasAccount?: boolean
  /** Desde cuándo tiene cuenta (cuando verificó su correo). */
  accountSince?: string | null
  lastLoginAt?: string | null
  /** La foto de su cuenta del sitio (la cambia él en «Mi cuenta → Perfil»). */
  avatarUrl?: string | null
}

/** Una compra suya, para la pestaña de Compras de su ficha. */
export type ContactPurchase = {
  id: string
  offerTitle: string
  /** Portada actual de la oferta; nula si no tiene o ya no existe. */
  imageUrl: string | null
  amountTotal: number
  currency: string
  /** Ya formateado por el servidor, como en Cobros. */
  formatted: string
  status: string
  createdAt: string
}

/** Una cita suya, para la pestaña de Reservas de su ficha. */
export type ContactBooking = {
  id: string
  offerTitle: string
  /** Portada actual del servicio; nula si no tiene o ya no existe. */
  imageUrl: string | null
  startsAt: string
  endsAt: string
  /** La zona en que se reservó, congelada: es la hora que vio el cliente. */
  timezone: string
  status: string
  /** Con quién: el estilista, la sala. Nulo si el recurso ya no existe. */
  resourceName: string | null
  createdAt: string
}

/** Una reprogramación de una cita, para la cronología de la persona. */
export type ContactBookingChange = {
  id: string
  bookingId: string
  offerTitle: string
  imageUrl: string | null
  timezone: string
  previousStartsAt: string | null
  startsAt: string
  actorName: string
  /* El id manda sobre el nombre copiado: con él se saca la foto y el nombre de
     hoy del equipo, y el copiado queda para quien ya no está. */
  actorUserId: string | null
  actorKind: 'team' | 'customer' | 'system'
  at: string
}

export type ContactQuery = {
  page?: number
  pageSize?: number
  q?: string
  sort?: string
  status?: string
  source?: string
  /** Cuenta del sitio: `yes`, `no` o `all`. */
  account?: string
}

export function listContacts(projectId: string, token: string, params: ContactQuery = {}) {
  return requestPage<ContactRecord>(`/api/projects/${projectId}/contacts${pageQuery(params)}`, token)
}

/** El detalle se pide por su cuenta: así un enlace pegado abre a la persona
 *  correcta aunque esté en la página cuarenta o la oculte el filtro activo. */
export function getContact(projectId: string, identity: string, token: string) {
  return request<ContactRecord & { activities: FormSubmission[]; purchases: ContactPurchase[]; bookings: ContactBooking[]; bookingChanges: ContactBookingChange[] }>(
    `/api/projects/${projectId}/contacts/${encodeURIComponent(identity)}`,
    token,
  )
}

/** Una membresía o paquete que puede incluir un servicio con reserva (fase 7). */
export type InclusionCandidate = {
  offerId: string
  title: string
  /** Por su precio: recurrente = membresía, único = paquete. */
  kind: 'membership' | 'pack'
  /** Membresía: reservas por periodo. Nulo = ilimitadas. */
  bookingsPerPeriod: number | null
  /** Paquete: clases por unidad comprada. */
  packCredits: number | null
  /** Paquete: días que valen. Nulo = no caducan. */
  packValidDays: number | null
}

export type BookingInclusions = { ready: boolean; allowed: boolean; chosen: string[]; candidates: InclusionCandidate[] }

/** `offerId` puede ser `new` mientras el servicio se está creando. */
export function getBookingInclusions(projectId: string, offerId: string, token: string) {
  return request<BookingInclusions>(`/api/projects/${projectId}/offers/${encodeURIComponent(offerId)}/inclusions`, token)
}

export function saveBookingInclusions(projectId: string, offerId: string, items: Array<Omit<InclusionCandidate, 'title' | 'kind'>>, token: string) {
  return request<{ ok: true }>(`/api/projects/${projectId}/offers/${encodeURIComponent(offerId)}/inclusions`, token, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  })
}

/** Una membresía o un paquete de clases, en la sección «Membresías». */
export type MembershipItem = {
  offerId: string
  title: string
  status: Offer['status']
  shortDescription: string
  image: string | null
  /** Todas las fotos, la primera es la portada. */
  images?: string[]
  kind: 'membership' | 'pack'
  price: { amount: number; currency: string; interval: OfferPrice['interval']; intervalCount: number } | null
  bookingsPerPeriod: number | null
  packCredits: number | null
  packValidDays: number | null
  /** Los servicios con reserva que incluye. */
  includes: string[]
  /** Cuántas personas la tienen activa ahora. */
  activeMembers: number
  /** Cuántas compras tiene (pedidos). Con alguna, el tipo ya no se cambia. */
  purchases?: number
  /** Productos digitales que da (migración 0056). */
  digitalIncludes?: string[]
  /** Descuento para miembros en el resto del catálogo (sólo membresías). */
  memberDiscountPercent?: number | null
  /** Nulo = todo el catálogo. */
  memberDiscountOfferIds?: string[] | null
}

export type MembershipsCatalog = {
  ready: boolean
  allowed: boolean
  items: MembershipItem[]
  /** Las clases y reservas que se pueden incluir. */
  bookings: Array<{ offerId: string; title: string; status: Offer['status']; image?: string | null }>
  /** Lo que un descuento de miembro puede cubrir: productos, servicios sin reserva, digitales. */
  discountables?: Array<{ offerId: string; title: string; status: Offer['status']; image: string | null }>
  /** La migración del descuento aplicada. */
  memberDiscountReady?: boolean
  /** Productos digitales que una membresía o un paquete puede incluir. */
  digitals?: Array<{ offerId: string; title: string; status: Offer['status']; image: string | null }>
  /** La migración de productos digitales incluidos (0056) aplicada. */
  digitalReady?: boolean
}

export function listMemberships(projectId: string, token: string) {
  return request<MembershipsCatalog>(`/api/projects/${projectId}/memberships`, token)
}

export function saveMembership(projectId: string, offerId: string, input: { bookingsPerPeriod: number | null; packCredits: number | null; packValidDays: number | null; includes: string[]; memberDiscountPercent?: number | null; memberDiscountOfferIds?: string[] | null; digitalIncludes?: string[] }, token: string) {
  return request<{ ok: true; includes: string[] }>(`/api/projects/${projectId}/memberships/${encodeURIComponent(offerId)}`, token, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

/** La cuenta del sitio de un contacto y sus membresías (fase 6 de las cuentas de clientes). */
export type ContactAccount = {
  /** Sus paquetes, con lo que le queda. */
  packs?: Array<{ orderId: string; title: string; boughtAt: string; remaining: number | null; limit: number | null; expiresAt: string | null }>
  /** Sus membresías gratis (unirse = reclamarla). */
  freeMemberships?: Array<{ orderId: string; title: string; joinedAt: string; active: boolean; percent: number | null }>
  account: {
    status: 'active' | 'blocked'
    since: string | null
    lastLoginAt: string | null
    /** Sesiones abiertas ahora mismo. */
    sessions: number
    marketingOptIn: boolean
  } | null
  memberships: Array<{
    id: string
    offerTitle: string
    /** El de Stripe: active, trialing, past_due, unpaid, incomplete, paused, canceled… */
    status: string
    /** Ya formateado con su moneda; vacío si no se sabe. */
    price: string
    interval: string
    intervalCount: number
    currentPeriodEnd: string | null
    cancelAt: string | null
    canceledAt: string | null
  }>
}

export function getContactAccount(projectId: string, identity: string, token: string) {
  return request<ContactAccount>(`/api/projects/${projectId}/contacts/${encodeURIComponent(identity)}/account`, token)
}

export function revokeContactSessions(projectId: string, identity: string, token: string) {
  return request<{ ok: true }>(`/api/projects/${projectId}/contacts/${encodeURIComponent(identity)}/account/sessions/revoke`, token, { method: 'POST' })
}

export function setContactAccountBlocked(projectId: string, identity: string, blocked: boolean, token: string) {
  return request<{ status: 'active' | 'blocked' }>(`/api/projects/${projectId}/contacts/${encodeURIComponent(identity)}/account/block`, token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ blocked }),
  })
}

/** «Dar acceso» desde Contactos (como el «Grant offer» de Kajabi). */
export type AccessGrantKind = 'membership' | 'pack' | 'digital'
export type AccessGrant = {
  id: string
  offerId: string | null
  title: string
  kind: AccessGrantKind
  until: string | null
  note: string
  grantedByName: string
  createdAt: string
  revokedAt: string | null
  active: boolean
}
export type GrantableOffer = { offerId: string; title: string; kind: AccessGrantKind; status: Offer['status']; image: string | null }

export function getContactGrants(projectId: string, identity: string, token: string) {
  return request<{ ready: boolean; canGrant: boolean; grants: AccessGrant[] }>(`/api/projects/${projectId}/contacts/${encodeURIComponent(identity)}/grants`, token)
}

export function listGrantableOffers(projectId: string, token: string) {
  return request<GrantableOffer[]>(`/api/projects/${projectId}/grantable`, token)
}

export function grantContactAccess(projectId: string, identity: string, input: { offerId: string; until: string | null; note: string; notify: boolean; name: string }, token: string) {
  return request<{ ok: true; id: string }>(`/api/projects/${projectId}/contacts/${encodeURIComponent(identity)}/grants`, token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
}

export function revokeContactAccess(projectId: string, identity: string, grantId: string, token: string) {
  return request<{ ok: true }>(`/api/projects/${projectId}/contacts/${encodeURIComponent(identity)}/grants/${encodeURIComponent(grantId)}/revoke`, token, { method: 'POST' })
}

export function cancelContactMembership(projectId: string, identity: string, membershipId: string, when: 'period_end' | 'now', token: string) {
  return request<{ ok: true }>(`/api/projects/${projectId}/contacts/${encodeURIComponent(identity)}/memberships/${encodeURIComponent(membershipId)}/cancel`, token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ when }),
  })
}

export function createContact(
  projectId: string,
  input: Pick<ManualContact, 'name' | 'email' | 'phone' | 'note'>,
  token: string,
) {
  return request<ManualContact>(`/api/projects/${projectId}/contacts`, token, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/** Un mensaje sin leer, para la campana. Uno por aviso, no un recuento. */
export type UnreadSubmission = {
  id: string
  projectId: string
  projectName: string
  formName: string
  name: string
  email: string
  createdAt: string
  /** Visto no es descartado: sigue en la campana, apagado. */
  seen: boolean
}

/* El proyecto abierto acota los tres avisos de trabajo. Sin él —el panel de
   inicio— el servidor no devuelve ninguno: ver la nota en `NotificationsMenu`. */
/** La dirección de un aviso de trabajo, acotada al proyecto abierto si lo hay. */
function noticePath(base: string, projectId?: string): string {
  return projectId ? `${base}?project=${encodeURIComponent(projectId)}` : base
}

export function listUnreadSubmissions(token: string, projectId?: string) {
  return request<UnreadSubmission[]>(noticePath('/api/account/unread-submissions', projectId), token)
}

/** Una venta que todavía no se ha mirado, para la campana. */
export type UnreadSale = {
  id: string
  projectId: string
  projectName: string
  offerTitle: string
  amountTotal: number
  currency: string
  /** Ya formateado por el servidor, como en Cobros. */
  formatted: string
  buyerName: string
  buyerEmail: string
  createdAt: string
  /** Visto no es descartado: sigue en la campana, apagado. */
  seen: boolean
}

export function listUnreadSales(token: string, projectId?: string) {
  return request<UnreadSale[]>(noticePath('/api/account/unread-sales', projectId), token)
}

/**
 * Un cambio que hizo el cliente por su cuenta, desde su enlace personal.
 *
 * `kind` lo decide el servidor y no este lado: una cita movida y después
 * cancelada conserva las dos marcas, y elegir aquí abriría la puerta a que la
 * campana y el correo cuenten cosas distintas de lo mismo.
 */
export type BookingChangeNotice = {
  id: string
  projectId: string
  projectName: string
  offerId: string | null
  offerTitle: string
  customerName: string
  startsAt: string
  timezone: string
  status: string
  cancelledAt: string | null
  cancellationReason: string
  rescheduledAt: string | null
  rescheduledFromStartsAt: string | null
  updatedAt: string
  /** Con quién es. Sólo con equipo; opcional porque un worker viejo no lo manda. */
  withName?: string | null
  withPhoto?: string | null
  kind: 'cancelled' | 'rescheduled' | 'created'
  /** Visto no es descartado: sigue en la campana, apagado. */
  seen: boolean
}

export function listBookingChanges(token: string, projectId?: string) {
  return request<BookingChangeNotice[]>(noticePath('/api/account/booking-changes', projectId), token)
}

/** Una cita cuyo reembolso automático se rindió. Ver `/refund-failed`. */
export type RefundFailedNotice = {
  id: string
  projectId: string
  offerTitle: string
  customerName: string
  refundAmount: number | null
  currency: string | null
  updatedAt: string
}

export function listRefundFailed(token: string, projectId?: string) {
  return request<RefundFailedNotice[]>(noticePath('/api/account/refund-failed', projectId), token)
}

/** Un producto físico al que le quedan pocas unidades (o ninguna). Ver `/low-stock`. */
export type LowStockNotice = {
  offerId: string
  projectId: string
  title: string
  /** Con variantes, la combinación que se acaba («M / Verde»); null sin variantes. */
  variantId: string | null
  variant: string | null
  stock: number
  /** La última venta, que es lo que lo provocó. */
  at: string
}

export function listLowStock(token: string, projectId?: string) {
  return request<LowStockNotice[]>(noticePath('/api/account/low-stock', projectId), token)
}

/** Apaga el aviso de un cambio. Se llama al abrir la cita, no la campana. */
export function markBookingChangeSeen(bookingId: string, token: string, projectId?: string) {
  return request<{ ok: true }>(`/api/account/booking-changes/${bookingId}/seen`, token, {
    method: 'POST',
  }).then((result) => {
    notifyDashboardItemRead({ token, ...(projectId ? { projectId } : {}), resource: 'bookings', id: bookingId })
    return result
  })
}

/** Apaga el aviso de una venta. Se llama al abrir su ficha. */
export function markOrderSeen(projectId: string, orderId: string, token: string) {
  return request<{ ok: true }>(`/api/projects/${projectId}/commerce/orders/${orderId}/seen`, token, {
    method: 'POST',
  }).then((result) => {
    notifyDashboardItemRead({ token, projectId, resource: 'orders', id: orderId })
    return result
  })
}

/**
 * Borra a una persona: su ficha manual y todos sus mensajes.
 *
 * `identity` es el id que usa el panel (`email:…`, `manual:…`, `submission:…`).
 */
export function deleteContact(projectId: string, identity: string, token: string) {
  return request<{ ok: true }>(`/api/projects/${projectId}/contacts/${encodeURIComponent(identity)}`, token, {
    method: 'DELETE',
  })
}

export type SubmissionQuery = {
  page?: number
  pageSize?: number
  q?: string
  form?: string
  status?: string
  sort?: string
}

export function listSubmissions(projectId: string, token: string, params: SubmissionQuery = {}) {
  /* `hiddenOverLimit`: los que llegaron pasado el tope del plan gratuito. Se
     guardan pero no se enseñan; el panel sólo sabe cuántos son. */
  return requestPage<FormSubmission>(`/api/projects/${projectId}/submissions${pageQuery(params)}`, token) as Promise<Paged<FormSubmission> & { hiddenOverLimit?: number }>
}

/** Un envío por su id, para cuando el enlace apunta a una página que no está cargada. */
export function getSubmission(projectId: string, submissionId: string, token: string) {
  return request<FormSubmission>(`/api/projects/${projectId}/submissions/${submissionId}`, token)
}

export function setSubmissionStatus(
  projectId: string,
  submissionId: string,
  status: FormSubmission['status'],
  token: string,
) {
  return request<{ ok: true }>(`/api/projects/${projectId}/submissions/${submissionId}`, token, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  }).then((result) => {
    if (status !== 'unread') notifyDashboardItemRead({ token, projectId, resource: 'forms', id: submissionId })
    return result
  })
}

export function deleteSubmission(projectId: string, submissionId: string, token: string) {
  return request<{ ok: true }>(`/api/projects/${projectId}/submissions/${submissionId}`, token, {
    method: 'DELETE',
  })
}

// ─── Ventas y cobros: la cuenta de Stripe del cliente ────────────────────────
// No confundir con `/api/billing`, que es lo que él nos paga a nosotros. Esto
// es la cuenta conectada con la que cobra a SUS compradores.

/** Sin cuenta · alta a medias · alta hecha pero sin poder cobrar · lista. */
export type ConnectStatus = 'none' | 'pending' | 'restricted' | 'ready'

export type ConnectedAccount = {
  stripeAccountId: string
  chargesEnabled: boolean
  payoutsEnabled: boolean
  detailsSubmitted: boolean
  country: string
  defaultCurrency: string
  /** Lo que Stripe aún pide, tal cual viene, para poder enumerarlo. */
  requirements: {
    currentlyDue?: string[]
    eventuallyDue?: string[]
    pastDue?: string[]
    disabledReason?: string | null
    currentDeadline?: number | null
  }
  connectedAt: string
}

export type StripeConnection = { status: ConnectStatus; account: ConnectedAccount | null }

// ─── Integraciones de terceros ───────────────────────────────────────────────

/**
 * El estado de una cuenta conectada. Nunca la credencial.
 *
 * `hint` son los últimos cuatro caracteres de la clave (`••••a1b2`): bastan para
 * reconocer cuál puso y no sirven para usarla. La clave entera no vuelve a salir
 * del Worker una vez guardada — ver `services/secretBox.ts`.
 */
export type IntegrationState = {
  connected: boolean
  /** 'active' | 'invalid' — 'invalid' lo pone un 401 del proveedor. */
  status: string
  hint: string
  /** Por qué dejó de funcionar, cuando el proveedor lo dijo. */
  lastError: string
  updatedAt: string
}

export type ProjectIntegrations = Partial<Record<'omnisend', IntegrationState>>

export function getIntegrations(projectId: string, token: string) {
  return request<ProjectIntegrations>(`/api/projects/${projectId}/integrations`, token)
}

/**
 * Conecta la cuenta, o cambia la clave.
 *
 * El servidor la comprueba contra Omnisend ANTES de guardarla, así que esto
 * tarda lo que tarde esa llamada y puede fallar con un motivo que merece
 * enseñarse tal cual: distingue «Omnisend no reconoce esa clave» de «no se pudo
 * comprobar», que son dos cosas que se arreglan de forma distinta.
 */
export function connectIntegration(projectId: string, provider: 'omnisend', apiKey: string, token: string) {
  return request<{ connected: boolean; status: string; hint: string }>(
    `/api/projects/${projectId}/integrations/${provider}`,
    token,
    { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ apiKey }) },
  )
}

export function disconnectIntegration(projectId: string, provider: 'omnisend', token: string) {
  return request<{ connected: boolean }>(
    `/api/projects/${projectId}/integrations/${provider}`,
    token,
    { method: 'DELETE' },
  )
}

/**
 * El estado de la cuenta de Stripe del proyecto.
 *
 * `fresh` obliga al servidor a volver a preguntárselo a Stripe en vez de
 * contestar con lo último que sabía. Sólo lo pide la pantalla de ajustes de
 * Stripe, que es donde alguien está terminando el alta: en el resto, esa
 * llamada a un servidor de fuera se metía en el camino de pintar la pantalla.
 */
export function getStripeConnection(projectId: string, token: string, options?: { fresh?: boolean }) {
  const query = options?.fresh ? '?fresh=1' : ''
  return request<StripeConnection>(`/api/projects/${projectId}/commerce/stripe${query}`, token)
}

/**
 * Empieza o retoma el alta. Devuelve un enlace de Stripe de un solo uso, así que
 * se pide en el momento de pulsar y no se guarda.
 */
export function startStripeOnboarding(
  projectId: string,
  token: string,
  /* `returnTo` decide a dónde devuelve Stripe. Es una lista cerrada y no una
     dirección: el servidor arma las dos que existen. */
  body: { country?: string; email?: string; returnTo?: 'payments' | 'handover' } = {},
) {
  return request<{ url: string }>(`/api/projects/${projectId}/commerce/stripe/connect`, token, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export function disconnectStripe(projectId: string, token: string) {
  return request<{ status: ConnectStatus }>(`/api/projects/${projectId}/commerce/stripe`, token, {
    method: 'DELETE',
  })
}

/**
 * El permiso para incrustar la vista de Stripe de un cobro.
 *
 * Se pide en el momento y no se guarda: el secreto caduca, y la propia
 * biblioteca de Connect vuelve a llamar aquí cuando eso ocurre.
 */
export function createStripeAccountSession(projectId: string, token: string) {
  return request<{ clientSecret: string }>(`/api/projects/${projectId}/commerce/stripe/account-session`, token, {
    method: 'POST',
  })
}

/**
 * El saldo y el próximo depósito, en cifras.
 *
 * Los importes vienen en la unidad mínima y en entero, como el resto del dinero
 * de esta casa. `arrivalDate` son segundos, tal como los da Stripe: la fecha se
 * arma en el navegador, que es quien sabe en qué zona horaria está el vendedor.
 */
export type StripeBalance = {
  currency: string
  /** Lo que ya se puede depositar. */
  available: number
  /** Cobrado, pero aún dentro del plazo que Stripe retiene. */
  pending: number
  nextPayout: { amount: number; arrivalDate: number; status: 'pending' | 'in_transit' } | null
  /** Sin esto, el saldo es un número que no va a moverse. */
  payoutsEnabled: boolean
  /** Cada cuánto deposita la cuenta: `daily` | `weekly` | `monthly` | `manual`. */
  schedule?: string | null
  /**
   * Cuándo tocaría el siguiente, en segundos, cuando aún no existe el depósito.
   *
   * Es una estimación del servidor a partir del calendario de la cuenta y hay
   * que enseñarla como tal: no lleva festivos ni ventanas bancarias. Llega en
   * `null` en cuanto hay un depósito real, porque entonces la fecha buena es la
   * suya.
   */
  estimatedArrival?: number | null
}

export function getStripeBalance(projectId: string, token: string) {
  return request<StripeBalance>(`/api/projects/${projectId}/commerce/stripe/balance`, token)
}

// ─── Ofertas ────────────────────────────────────────────────────────────────
// Lo que el cliente vende. El importe va siempre en la unidad mínima de la
// moneda y en entero, como lo maneja Stripe: 199.00 MXN son 19900.

export type OfferPrice = {
  id: string
  unitAmount: number
  currency: string
  interval: 'one_time' | 'week' | 'month' | 'year'
  /**
   * Cada cuántos `interval` se cobra. Uno salvo trimestral (3) y semestral (6).
   *
   * Trimestral no es un `interval` propio a propósito: es «mes × 3», que es
   * como lo expresa Stripe. Ver `PriceInterval` en el worker.
   */
  intervalCount: number
  isDefault: boolean
  active: boolean
  /** Ya formateado por el servidor, para no repetir la regla de cada moneda. */
  formatted: string
}

/** Lo generado por una oferta en una moneda. Neto: lo cobrado menos lo devuelto. */
export type OfferRevenue = {
  currency: string
  amount: number
  /** Ya formateado por el servidor, como los precios. */
  formatted: string
}

/**
 * Los tipos que puede tener una pregunta del formulario de reserva.
 *
 * Es el mismo vocabulario que los campos del bloque «formulario» del editor, a
 * propósito: quien ya montó un formulario de contacto en su sitio no tiene que
 * aprender otro para preguntar lo mismo al reservar. El worker valida contra
 * esta misma lista antes de guardar.
 */
export const BOOKING_FIELD_TYPES = ['text', 'email', 'tel', 'textarea', 'select', 'checkbox'] as const

export type BookingFieldType = (typeof BOOKING_FIELD_TYPES)[number]

/** Una pregunta extra del formulario de reserva. */
export type BookingFormField = {
  /** Identificador estable dentro del servicio. Es lo que viaja en el POST. */
  name: string
  label: string
  type: BookingFieldType
  required: boolean
  /** Sólo con `select`. Vacío en los demás. */
  options: string[]
}

/** Lo que respondió alguien, con la pregunta tal como se le hizo. */
export type BookingFieldAnswer = {
  name: string
  label: string
  value: string
}

/** Un entregable de una oferta digital, tal y como lo ve el panel. */
export type OfferFile = {
  id: string
  name: string
  /** En bytes: cuenta en la cuota de peso del proyecto, como las imágenes. */
  size: number
  contentType: string
}

/**
 * Una opción de un producto con variantes: su nombre y sus valores. De cada
 * valor, el tono de su muestra (`swatches`) y la foto que se enseña al
 * elegirlo (`images`), si se eligieron.
 */
export type OfferVariantOption = {
  name: string
  values: string[]
  swatches?: Record<string, string> | undefined
  images?: Record<string, string> | undefined
}

/** Una combinación («M / Rojo») con sus existencias (null = sin límite) y su SKU. */
export type OfferVariant = {
  id: string
  options: string[]
  label: string
  stock: number | null
  sku: string
  /** Falso = esa combinación no se vende. */
  active: boolean
  /** En centavos; null = el precio del producto. */
  price?: number | null | undefined
  /** Su ficha: foto propia, «antes» tachado, costo (sólo panel) y código de barras. */
  image?: string | null | undefined
  compareAtPrice?: number | null | undefined
  cost?: number | null | undefined
  barcode?: string | undefined
}

export type Offer = {
  id: string
  /**
   * «Por completar»: la empezó la IA con datos de ejemplo al crear el sitio.
   * Abre su asistente en modo completar, y el servidor no deja activarla hasta
   * que se termine.
   */
  setupPending?: boolean
  title: string
  slug: string
  description: string
  shortDescription?: string
  images: string[]
  /** Etiquetas libres. Opcional: un worker viejo no las manda. */
  tags?: string[]
  /** La marca (Nike, Apple…), sólo en productos físicos y digitales. */
  brand?: string
  /** El precio «antes», tachado (unidades mínimas). Null = sin. */
  compareAtPrice?: number | null
  /**
   * Lo que se entrega al comprarla, si es un producto digital.
   *
   * Sin la clave de almacenamiento: el panel lista, sube y borra, y para eso le
   * basta el nombre y el peso. Dónde vive el archivo no sale del servidor — se
   * entrega con un enlace firmado y caducable. */
  digitalFiles: OfferFile[]
  type: 'digital' | 'service' | 'course' | 'physical'
  /** Existencias de un producto físico; null = sin límite. Con variantes no manda: ver `variants`. */
  stock: number | null
  /** Las opciones de un producto físico con variantes (talla, color…); vacío = sin variantes. */
  variantOptions?: OfferVariantOption[]
  /** Cada combinación de esas opciones, con sus existencias. Vacío = sin variantes. */
  variants?: OfferVariant[]
  /** Lo que queda en total de un producto físico (variantes sumadas); null = sin límite. */
  availableStock?: number | null
  /** Una membresía o un paquete de clases (tiene su sección propia, fuera de «Servicios»). */
  membership?: boolean
  status: 'draft' | 'active' | 'archived'
  stripeProductId: string | null
  /**
   * A dónde va el comprador después de pagar.
   *
   * `default` es la página de gracias que sirve el worker: existe siempre y
   * muestra el importe real. `page` apunta al `id` de una página del sitio y
   * `url` a una dirección externa.
   */
  thankYouMode: 'default' | 'page' | 'url'
  thankYouPageId: string | null
  thankYouUrl: string | null
  /**
   * La agenda de esta oferta. `durationMinutes` en null es el interruptor: sin
   * duración no hay calendario y la oferta se vende como cualquier otra.
   */
  booking: {
    durationMinutes: number | null
    bufferBeforeMinutes: number
    bufferAfterMinutes: number
    stepMinutes: number | null
    capacity: number
    minNoticeMinutes: number
    maxAdvanceDays: number
    paymentMode: 'prepaid' | 'deposit' | 'onsite' | 'membership'
    depositAmount: number | null
    resourceId: string | null
    /** Quiénes lo dan. Vacía = `resourceId`, o el primero del equipo. Opcional: un worker viejo no la manda. */
    resourceIds?: string[]
    /** Cuántas personas puede apuntar una reserva. 1 = sin selector. */
    maxParty?: number
    /** person = precio × personas; booking = un precio por reserva. */
    pricePer?: 'person' | 'booking'
    /** `time` = por horas; `day` = día completo o varios días. Opcional: un worker viejo no lo manda. */
    unit?: 'time' | 'day'
    /** Por días: cuántos. Iguales = fijo; distintos = los elige el cliente. */
    daysMin?: number
    daysMax?: number
    /** Cuántos lugares quedan, en la página: `all` | `low` | `none`. */
    spotsDisplay?: 'all' | 'low' | 'none'
    /** Quién atiende con «Cualquiera disponible». Opcional: un worker viejo no lo manda. */
    assignment?: BookingAssignment
    /**
     * Las preguntas extra del formulario de reserva.
     *
     * Opcional porque un worker anterior a esta columna no la manda, y darla
     * por segura dejaba el panel en blanco en cuanto faltaba.
     */
    formFields?: BookingFormField[]
    /**
     * Enlace a los términos, o null. La página de reserva los enseña como aviso
     * junto al botón; el consentimiento con casilla se pide, cuando hace falta,
     * con una pregunta de tipo casilla obligatoria.
     */
    termsUrl?: string | null
    /** Cuánto se devuelve, en porcentaje: a tiempo y fuera de plazo. */
    refundPercent: number
    refundLatePercent: number
    /** Al cancelar, véltica emite el reembolso por Stripe. Opcional: un worker viejo no lo manda (= manual). */
    refundAuto?: boolean
    /** Horas antes hasta las que el cliente puede cancelar. 0 = sin límite. */
    cancelWindowHours: number
    /** Igual para mover. Nulo hereda el de cancelar. */
    rescheduleWindowHours: number | null
    /**
     * El correo al cliente antes de la cita, y cuántas horas antes.
     * Opcionales por lo mismo que `formFields`: un worker sin la columna no los manda.
     */
    reminderEnabled?: boolean
    reminderHours?: number
    /** Qué se le ofrece al cliente pasado el plazo. */
    latePolicy: 'contact' | 'move' | 'none'
    lateRefillRefund: boolean
    lateCredit: boolean
    lateForgiveFirst: boolean
    /** Vacío hereda el correo de avisos del proyecto. */
    contactEmail: string
    contactPhone: string
  }
  /**
   * La página de reserva, servida en `/book/<slug>` del sitio publicado.
   *
   * Título, descripción, imagen y precio salen de la propia oferta; aquí sólo
   * está lo que existe únicamente en esa página.
   */
  bookingPage: {
    logo: string | null
    /** Hex `#rrggbb`. Nulo = el color por defecto. */
    accent: string | null
    theme: 'auto' | 'light' | 'dark'
  }
  /**
   * Los ajustes de su página pública: cómo se titula fuera y quién la encuentra.
   *
   * Los tres tipos de oferta tienen página propia —comprar, reclamar o
   * reservar— y esto vale para las tres. VACÍO SIGNIFICA HEREDAR: sin título de
   * SEO manda el de la oferta, sin descripción los primeros 160 caracteres de la
   * suya, sin imagen su portada. Lo que se guarda es la excepción.
   *
   * Opcional porque un worker anterior a estas columnas no lo manda, y darlo por
   * seguro dejaba la pestaña en blanco en cuanto faltara.
   */
  page?: {
    seoTitle: string
    seoDescription: string
    /** Vacío = la propia dirección de la página. */
    canonicalUrl: string
    /** Atada al SEO mientras nadie la desate; es como nace. */
    ogSameAsSeo: boolean
    ogTitle: string
    ogDescription: string
    /** Vacío = la portada de la oferta. */
    ogImage: string
    indexable: boolean
    inSitemap: boolean
  }
  /**
   * El aspecto de su página: ids de token del tema, vacío = como hoy. Opcional
   * por lo mismo que `page`: un worker anterior a la columna no lo manda.
   */
  pageStyle?: OfferPageStyle
  /** Lo que vale cada campo personalizado de su tipo («Ingredientes»…), por id. */
  customValues?: Record<string, string>
  /**
   * A quién se le avisa de lo que pasa con esta oferta, y si se le avisa.
   *
   * `email` vacío significa «al correo de tu cuenta», que es de donde partía
   * esto cuando el destinatario era uno para todo el proyecto. Opcional porque
   * un worker anterior a estas columnas no lo manda, y darlo por seguro dejaría
   * la tarjeta en blanco.
   */
  notify?: {
    enabled: boolean
    email: string
    /** Con equipo: a los correos de arriba, o a la persona de cada cita. Opcional: un worker viejo no lo manda. */
    to?: 'list' | 'person'
  }
  /** Cobros pagados de esta oferta. Los de una oferta borrada no cuentan aquí. */
  /**
   * Cuántas se han vendido. AUSENTE para quien lee las ofertas desde la agenda:
   * un servicio reservable es una oferta, pero quien lleva el calendario puede
   * no tener acceso al dinero. Ver `offerDto` en el worker.
   */
  salesCount?: number
  /**
   * Lo que lleva generado, neto de reembolsos y con una línea por moneda.
   *
   * Una lista y no un número porque un sitio puede cobrar en varias y sumarlas
   * daría una cifra falsa — la misma salida que toma el total de Cobros. Viene
   * de mayor a menor, y vacía mientras no haya vendido nada.
   */
  revenue: OfferRevenue[]
  createdAt: string
  updatedAt: string
  prices: OfferPrice[]
}

/**
 * Una venta. La escribe el webhook de Stripe, nunca el panel.
 *
 * `offerTitle` es una copia del momento de la compra y no el título actual de
 * la oferta: un cobro tiene que seguir diciendo qué se vendió aunque la oferta
 * se renombre o se retire, y `offerId` puede ser null si se borró.
 */
/** Una línea de un pedido del carrito. En la ficha llega con su foto y sus importes. */
export type OrderItem = {
  offerId?: string | null
  title: string
  variantLabel: string | null
  quantity: number
  kind: 'physical' | 'digital' | 'service'
  image?: string | null
  unitFormatted?: string
  totalFormatted?: string
}

export type Order = {
  id: string
  seenAt?: string | null
  /** El número del pedido en la tienda (#1001…). Nulo hasta aplicar su migración. */
  number?: number | null
  offerId: string | null
  offerTitle: string
  /**
   * La portada de la oferta, para reconocer la fila de un vistazo.
   *
   * Se lee de la oferta viva, no se copió al vender como el título: es
   * decoración y no registro. Nula si la oferta ya no existe.
   */
  offerImage: string | null
  amountTotal: number
  currency: string
  /** Ya formateado por el servidor, como en los precios. */
  formatted: string
  /** Una cortesía dada desde Contactos (sin cobro). */
  granted?: boolean
  /** paid | partially_refunded | refunded. Lo decide el webhook del reembolso. */
  status: string
  /** Lo devuelto, en la unidad mínima. Cero cuando no se devolvió nada. */
  amountRefunded: number
  /**
   * La disputa, si la hay. `null` es «nunca la hubo», que es lo normal.
   *
   * Aparte de `status` a propósito: aquél dice cuánto del dinero se quedó, y una
   * venta puede estar disputada y parcialmente devuelta a la vez.
   */
  disputeStatus: string | null
  /** Hasta cuándo hay para responder. `null` si no hay plazo o ya se cerró. */
  disputeDueBy: string | null
  /** Lo disputado, en la unidad mínima. No siempre es el total de la venta. */
  disputeAmount: number
  buyerEmail: string | null
  /** Quien compró: el nombre de su cuenta del sitio, o el de la tarjeta si no tiene. */
  buyerName: string | null
  /** El nombre que venía en la tarjeta (puede no ser quien compró). */
  cardName?: string | null
  createdAt: string
  /** Producto físico: cómo va el envío. `null` si no hay nada que enviar. */
  fulfillmentStatus?: FulfillmentStatus | null
  shippingName?: string | null
  shippingCountry?: string | null
  /** Cuántas unidades (1 en todo lo de antes) y de qué variante («M / Rojo»). */
  quantity?: number
  variantLabel?: string | null
  /** Un pedido del carrito: lo que lleva, línea por línea. `null` en los de una sola oferta. */
  items?: OrderItem[] | null
  /** El resto del envío, para exportar a CSV. */
  shippingPhone?: string | null
  shippingAddress?: ShippingAddress | null
  shippingRateName?: string | null
  carrier?: string | null
  trackingNumber?: string | null
}

/** El estado del envío de un pedido físico. Ver `routes/shipping.ts`. */
export type FulfillmentStatus = 'unfulfilled' | 'shipped' | 'delivered' | 'ready_for_pickup' | 'picked_up'

export type ShippingAddress = {
  line1: string | null
  line2: string | null
  city: string | null
  state: string | null
  postalCode: string | null
  country: string | null
}

/** El envío de un pedido, como lo devuelve la ficha. */
export type OrderShipping = {
  status: FulfillmentStatus
  name: string | null
  phone: string | null
  address: ShippingAddress | null
  /** Ya en renglones, para copiarla a una etiqueta. */
  formattedAddress: string
  rateName: string | null
  amountFormatted: string
  carrier: string | null
  trackingNumber: string | null
  trackingUrl: string | null
  shippedAt: string | null
  deliveredAt: string | null
  restockedAt: string | null
}

/** Los estados en los que el vendedor todavía puede defender la venta. */
export const DISPUTE_NEEDS_ACTION = ['warning_needs_response', 'needs_response']

export function disputeNeedsAction(status: string | null | undefined): boolean {
  return Boolean(status && DISPUTE_NEEDS_ACTION.includes(status))
}

/**
 * La disputa que ya se respondió y todavía no tiene veredicto.
 *
 * No es lo mismo que no tenerla y no es lo mismo que deberla: el dinero sigue
 * retenido y puede volver o no. Sin esto, la venta salía del rojo al responder y
 * caía directamente en «Pagado», que es afirmar que el cobro está firme cuando
 * lo está decidiendo el banco del comprador — y quien respondió se quedaba sin
 * saber si su respuesta llegó a alguna parte.
 *
 * Aparte de `DISPUTE_NEEDS_ACTION` y no dentro: aquello es una cola de trabajo
 * —lo que hay que atender antes de una fecha— y esto es un estado. Mezclarlas
 * pondría en el aviso rojo casos donde no hay nada que responder, y un aviso que
 * pide atención para algo que no la admite enseña a ignorar el aviso.
 */
export const DISPUTE_UNDER_REVIEW = ['warning_under_review', 'under_review']

export function disputeUnderReview(status: string | null | undefined): boolean {
  return Boolean(status && DISPUTE_UNDER_REVIEW.includes(status))
}

/**
 * Una disputa abierta, tal como la enseña el aviso de arriba del panel.
 *
 * El `id` es el del PEDIDO, no el de la disputa: el aviso lleva a la ficha del
 * cobro, que es la que sabe qué oferta era y quién compró. Stripe sólo conoce
 * el cargo.
 */
export type OpenDispute = {
  id: string
  offerTitle: string
  buyerName: string | null
  buyerEmail: string | null
  disputeStatus: string
  disputeDueBy: string | null
  disputeAmount: number
  /** Lo disputado, ya formateado por el servidor. */
  formatted: string
  currency: string
}

/**
 * Lo que reclama atención ahora mismo. Vacío es la respuesta normal.
 *
 * Endpoint propio y no un filtro sobre `listOrders`: lo pregunta el aviso en
 * cada carga de cualquier sección, y traerse doscientos cobros para descubrir
 * que no hay ninguna disputa sería pagar el historial entero por un sí o no.
 */
export function listOpenDisputes(projectId: string, token: string) {
  return request<OpenDispute[]>(`/api/projects/${projectId}/commerce/disputes`, token)
}

/**
 * Lo que Stripe sabe del cobro y nosotros no guardamos.
 *
 * No se guarda a propósito: reembolsos, disputas y recibos cambian después de
 * la venta, y una copia nuestra envejecería sin avisar. Puede venir `null` si
 * Stripe no contesta — la ficha se abre igual con lo que hay en la base.
 */
export type OrderStripeDetail = {
  paymentIntentId: string | null
  chargeId: string | null
  receiptUrl: string | null
  cardBrand: string | null
  cardLast4: string | null
  /** La comisión de Stripe. La paga el vendedor, no la plataforma. */
  fee: number | null
  net: number | null
  amountRefunded: number
  disputed: boolean
}

/** Un cobro hermano: otra mensualidad de la misma suscripción. */
export type SubscriptionPayment = {
  id: string
  amountTotal: number
  formatted: string
  createdAt: string
}

export type OrderDetail = Order & {
  /** De ese total, lo que fue impuesto, ya escrito. Nulo si no se cobró impuesto. */
  taxFormatted?: string | null
  /** El código con el que se compró, y lo descontado ya escrito. */
  couponCode?: string | null
  discountFormatted?: string | null
  /** Nulo en una renovación: nadie pasó por la pasarela, la cobró Stripe solo. */
  stripeSessionId: string | null
  /** Nulo cuando NO HUBO pasarela: una oferta gratuita reclamada con formulario. */
  stripeAccountId: string | null
  /** Presente sólo si el cobro viene de una suscripción. */
  subscription: {
    id: string
    /** Qué número de cobro es éste dentro de la suscripción. */
    number: number
    payments: SubscriptionPayment[]
  } | null
  stripe: OrderStripeDetail | null
  /** Presente sólo en un producto físico. */
  shipping?: OrderShipping | null
  /** El historial del pedido, del más antiguo al más reciente. */
  events?: OrderEvent[]
}

/** Una línea del historial de un pedido. Ver `services/orderEvents.ts`. */
export type OrderEvent = {
  id: string
  kind: 'paid' | 'email_confirmation' | 'fulfillment' | 'email_shipment' | 'email_refund' | 'refunded' | 'restocked' | 'oversold'
  at: string
  actorKind: 'team' | 'customer' | 'system' | 'stripe'
  actorName: string
  meta: Record<string, unknown>
}

export function listOrders(projectId: string, token: string) {
  return request<Order[]>(`/api/projects/${projectId}/commerce/orders`, token)
}

/** Impuestos del checkout con Stripe Tax. Ver `services/tax.ts` en el worker. */
export type TaxSettingsStatus = {
  enabled: boolean
  status: 'active' | 'pending'
  missingFields: string[]
  taxBehavior: 'inclusive' | 'exclusive' | 'inferred_by_currency' | null
  activeRegistrations: number
}

export function getTaxSettings(projectId: string, token: string) {
  return request<TaxSettingsStatus>(`/api/projects/${projectId}/commerce/tax`, token)
}

export function setAutomaticTax(projectId: string, enabled: boolean, token: string) {
  return request<TaxSettingsStatus>(`/api/projects/${projectId}/commerce/tax`, token, {
    method: 'PATCH',
    body: JSON.stringify({ enabled }),
  })
}

export function getOrder(projectId: string, orderId: string, token: string) {
  return request<OrderDetail>(`/api/projects/${projectId}/commerce/orders/${orderId}`, token)
}

/** Una forma de envío: importe fijo (gratis desde un total, si se quiere) o recoger. */
export type ShippingRate = {
  id: string
  name: string
  kind: 'flat' | 'pickup'
  /** En la unidad mínima de la moneda del proyecto. */
  amount: number
  freeOver: number | null
  pickupAddress: string
  sort: number
  active: boolean
}

export type ShippingRateInput = Omit<ShippingRate, 'id' | 'sort'>

export type ShippingConfig = {
  /** A qué países se envía (ISO de dos letras). Por defecto, el del negocio. */
  countries: string[]
  /** Si un reembolso total devuelve la unidad a las existencias. */
  restockOnRefund: boolean
  rates: ShippingRate[]
  /** Cuántas formas de envío admite un pago (Stripe). */
  maxRates: number
  /** Las paqueterías cuyo enlace de seguimiento se arma solo. */
  carriers: { key: string; name: string }[]
}

export function getShipping(projectId: string, token: string) {
  return request<ShippingConfig>(`/api/projects/${projectId}/commerce/shipping`, token)
}

export function saveShippingSettings(projectId: string, patch: Partial<Pick<ShippingConfig, 'countries' | 'restockOnRefund'>>, token: string) {
  return request<Pick<ShippingConfig, 'countries' | 'restockOnRefund'>>(`/api/projects/${projectId}/commerce/shipping`, token, {
    method: 'PUT',
    body: JSON.stringify(patch),
  })
}

export function createShippingRate(projectId: string, input: ShippingRateInput, token: string) {
  return request<ShippingRate>(`/api/projects/${projectId}/commerce/shipping/rates`, token, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function updateShippingRate(projectId: string, rateId: string, patch: Partial<ShippingRateInput>, token: string) {
  return request<ShippingRate>(`/api/projects/${projectId}/commerce/shipping/rates/${rateId}`, token, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
}

export function deleteShippingRate(projectId: string, rateId: string, token: string) {
  return request<{ ok: true }>(`/api/projects/${projectId}/commerce/shipping/rates/${rateId}`, token, { method: 'DELETE' })
}

export type FulfillmentPatch = {
  status: FulfillmentStatus
  carrier?: string | null
  trackingNumber?: string | null
  trackingUrl?: string | null
  /** Avisar al comprador por correo al enviar o dejar listo para recoger. */
  notify?: boolean
}

/** La hoja de empaque del pedido, en HTML listo para imprimir. Ver `services/packingSlip.ts`. */
export function getPackingSlip(projectId: string, orderId: string, token: string) {
  return request<{ html: string }>(`/api/projects/${projectId}/commerce/orders/${orderId}/packing-slip`, token)
}

export function updateOrderFulfillment(projectId: string, orderId: string, patch: FulfillmentPatch, token: string) {
  return request<Pick<OrderShipping, 'carrier' | 'trackingNumber' | 'trackingUrl' | 'shippedAt' | 'deliveredAt'> & { fulfillmentStatus: FulfillmentStatus; notified: boolean }>(
    `/api/projects/${projectId}/commerce/orders/${orderId}/fulfillment`,
    token,
    { method: 'PATCH', body: JSON.stringify(patch) },
  )
}

/**
 * Analíticas de venta. Mismo tramo y misma comparación que las de tráfico —
 * comparten `AnalyticsQuery` y el selector de fechas de arriba.
 *
 * Todos los importes van en la unidad mínima de `currency` (centavos), como en
 * el resto del comercio, y todos son de ESA moneda: un sitio que cobre en
 * varias verá el resto en `otherCurrencies`, nunca sumado. Sumar monedas
 * distintas en un eje daría una cifra falsa.
 */
export type SalesAnalytics = {
  range: AnalyticsRange
  window?: { from: string; to: string; days: number }
  compare?: AnalyticsCompare
  comparison?: {
    from: string
    to: string
    /** Alineada por posición con `series`, igual que en tráfico. */
    series: { day: string; revenue: number; orders: number }[]
    totals: { revenue: number; orders: number }
  } | null
  timezone?: string
  /** La que más facturó en el tramo. Manda en todas las cifras de abajo. */
  currency: string
  /** Lo cobrado en las demás monedas, para poder decir qué queda fuera. */
  otherCurrencies: { currency: string; revenue: number; orders: number; formatted: string }[]
  /** Si el sitio vendió alguna vez: separa «aún no vendes» de «no en estas fechas». */
  everSold: boolean
  totals: {
    /**
     * Lo cobrado menos lo devuelto. NO descuenta la comisión de Stripe: no se
     * guarda al cobrar, se le pregunta a Stripe al abrir una ficha de cobro, y
     * restarla aquí sería una llamada de red por venta del tramo.
     */
    revenue: number
    revenuePrev: number
    /** Lo cobrado antes de descontar devoluciones. */
    gross: number
    grossPrev: number
    refunded: number
    refundedPrev: number
    refundedOrders: number
    orders: number
    ordersPrev: number
    customers: number
    customersPrev: number
    /** Compradores sin ninguna compra anterior al tramo. */
    newCustomers: number
    returningCustomers: number
    /** Ticket promedio: lo neto entre el número de ventas. */
    aov: number
    aovPrev: number
    subscriptionRevenue: number
    subscriptionOrders: number
    oneTimeRevenue: number
    oneTimeOrders: number
    disputedOrders: number
    disputedAmount: number
  }
  granularity?: 'day' | 'hour'
  series: { day: string; hour?: number; revenue: number; orders: number }[]
  topOffers: {
    /** `null` si la oferta se borró: sus cobros guardan el título de entonces. */
    offerId: string | null
    title: string
    image: string | null
    revenue: number
    orders: number
    formatted: string
  }[]
  topCustomers: { email: string | null; name: string | null; revenue: number; orders: number; formatted: string }[]
  /** Los últimos cobros del tramo, por fecha y sin mezclarlos en un total. */
  recentSales: {
    id: string
    offerId: string | null
    offerTitle: string
    buyerEmail: string | null
    buyerName: string | null
    createdAt: string
    formatted: string
  }[]
  /** paid | partially_refunded | refunded, con lo que quedó de cada grupo. */
  statuses: { status: string; orders: number; amount: number }[]
}

export function getSalesAnalytics(token: string, projectId: string, query: AnalyticsQuery) {
  const search = new URLSearchParams({ range: query.range })
  if (query.from) search.set('from', query.from)
  if (query.to) search.set('to', query.to)
  if (query.compare) search.set('compare', query.compare)
  return request<SalesAnalytics>(`/api/projects/${projectId}/commerce/analytics?${search.toString()}`, token)
}

/**
 * Analíticas de la agenda. Mismo tramo y misma comparación que las otras dos —
 * comparten `AnalyticsQuery` y el selector de fechas de arriba.
 *
 * Se miden por CUÁNDO ES LA CITA (`startsAt`) y no por cuándo se reservó: una
 * semana de trabajo son las citas que ocurrieron en ella. La otra lectura vuelve
 * en `leadTimeHours`, que es la distancia entre las dos fechas.
 *
 * Los apartados que caducaron sin pagarse no cuentan como citas ni como
 * cancelaciones —no llegaron a ser nada— y viajan aparte en `abandoned`.
 */
export type BookingsAnalytics = {
  range: AnalyticsRange
  window?: { from: string; to: string; days: number }
  compare?: AnalyticsCompare
  comparison?: {
    from: string
    to: string
    /** Alineada por posición con `series`, igual que en las otras dos. */
    series: { day: string; bookings: number; completed: number }[]
    totals: { bookings: number; completed: number }
  } | null
  timezone?: string
  /** La que más facturó en citas prepagadas. Manda en los importes de abajo. */
  currency: string
  otherCurrencies: { currency: string; revenue: number; bookings: number; formatted: string }[]
  /** Si la agenda tuvo alguna cita: separa «aún no te reservan» de «no en estas fechas». */
  everBooked: boolean
  totals: {
    bookings: number
    bookingsPrev: number
    completed: number
    completedPrev: number
    noShow: number
    noShowPrev: number
    cancelled: number
    cancelledPrev: number
    /** Con desenlace registrado — asistió o no vino. La base de `attendanceRate`. */
    resolved: number
    resolvedPrev: number
    /**
     * Tanto por uno sobre `resolved` y no sobre el total: una agenda llena de
     * citas futuras diría un 20% de asistencia sin que nadie haya faltado.
     */
    attendanceRate: number
    attendanceRatePrev: number
    cancellationRate: number
    cancellationRatePrev: number
    /** Dentro del tramo y todavía por ocurrir. */
    upcoming: number
    /** Ya pasaron y siguen sin resultado registrado. */
    pending: number
    /** Apartados caducados sin pagar. Fuera de todos los demás totales. */
    abandoned: number
    customers: number
    customersPrev: number
    newCustomers: number
    returningCustomers: number
    rescheduled: number
    /** Minutos de agenda, sobre huecos únicos: una clase de seis ocupa una hora. */
    minutes: number
    minutesPrev: number
    /** Antelación media con que se reserva, en horas. */
    leadTimeHours: number
    leadTimeHoursPrev: number
    /** De las órdenes ligadas. Lo cobrado en el local no deja orden y no suma. */
    revenue: number
    revenuePrev: number
    paidBookings: number
  }
  granularity?: 'day' | 'hour'
  series: { day: string; hour?: number; bookings: number; completed: number }[]
  topServices: {
    /** `null` si el servicio se borró: sus citas guardan el título de entonces. */
    offerId: string | null
    title: string
    image: string | null
    bookings: number
    completed: number
    noShow: number
    cancelled: number
  }[]
  topCustomers: { email: string | null; name: string | null; bookings: number; completed: number; noShow: number }[]
  /** Quién presta el servicio. Una sola fila en casi todos los sitios. */
  resources: { resourceId: string; name: string; bookings: number }[]
  /** held | confirmed | completed | cancelled | no_show. */
  statuses: { status: string; bookings: number }[]
  /** 0 = lunes … 6 = domingo. Siempre las siete, con ceros. */
  weekdays: { weekday: number; bookings: number }[]
}

export function getBookingsAnalytics(token: string, projectId: string, query: AnalyticsQuery) {
  const search = new URLSearchParams({ range: query.range })
  if (query.from) search.set('from', query.from)
  if (query.to) search.set('to', query.to)
  if (query.compare) search.set('compare', query.compare)
  return request<BookingsAnalytics>(`/api/projects/${projectId}/bookings/analytics?${search.toString()}`, token)
}

export type OfferDraft = {
  title: string
  description?: string
  shortDescription?: string
  images?: string[]
  type?: Offer['type']
  brand?: string
  tags?: string[]
  compareAtPrice?: number | null
  /** Existencias de un producto físico; null = sin límite. */
  stock?: number | null
  price?: { unitAmount: number; currency: string; interval: OfferPrice['interval']; intervalCount?: number }
}

/** Un cupón de descuento. El descuento vive en Stripe; ver `services/coupons.ts`. */
export type Coupon = {
  id: string
  code: string
  description: string
  discountType: 'percent' | 'amount'
  percentOff: number | null
  /** En la unidad mínima de `currency`. */
  amountOff: number | null
  currency: string
  /** «20%» o «$10.00», ya escrito como se escribe en la región. */
  discountLabel: string
  duration: 'once' | 'repeating' | 'forever'
  durationInMonths: number | null
  appliesToAll: boolean
  offerIds: string[]
  maxRedemptions: number | null
  expiresAt: string | null
  minimumAmount: number | null
  minimumLabel: string | null
  active: boolean
  /** Se suma al descuento de miembro (si no, se aplica el que más ahorra). */
  combinesWithMember?: boolean
  /** Uno solo, para la lista: apagado, vencido, agotado o activo. */
  status: 'active' | 'inactive' | 'expired' | 'exhausted'
  uses: number
  revenueLabel: string
  discountTotalLabel: string
  createdAt: string
  updatedAt: string
}

export type CouponInput = {
  code: string
  description: string
  discountType: 'percent' | 'amount'
  percentOff: number | null
  amountOff: number | null
  duration: 'once' | 'repeating' | 'forever'
  durationInMonths: number | null
  appliesToAll: boolean
  offerIds: string[]
  maxRedemptions: number | null
  expiresAt: string | null
  minimumAmount: number | null
  active: boolean
  combinesWithMember?: boolean
}

export function listCoupons(projectId: string, token: string) {
  return request<Coupon[]>(`/api/projects/${projectId}/coupons`, token)
}

export function createCoupon(projectId: string, input: CouponInput, token: string) {
  return request<Coupon>(`/api/projects/${projectId}/coupons`, token, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function updateCoupon(projectId: string, couponId: string, input: CouponInput, token: string) {
  return request<Coupon>(`/api/projects/${projectId}/coupons/${couponId}`, token, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

export function deleteCoupon(projectId: string, couponId: string, token: string) {
  return request<{ id: string }>(`/api/projects/${projectId}/coupons/${couponId}`, token, { method: 'DELETE' })
}

export function listOffers(projectId: string, token: string) {
  return request<Offer[]>(`/api/projects/${projectId}/commerce/offers`, token)
}

// ── Colecciones ──────────────────────────────────────────────────────────────
// Como las de Shopify: un grupo de ofertas con nombre, descripción, imagen y
// página. Manual (elegidas a mano) o automática (las que cumplen sus reglas).
// Ver `services/collections.ts` en el worker.

export type CollectionRuleField = 'kind' | 'price' | 'tag' | 'brand' | 'title' | 'free' | 'stock' | `field:${string}`
export type CollectionRuleOp = 'is' | 'isNot' | 'contains' | 'notContains' | 'lt' | 'gt'
export type CollectionRule = { field: CollectionRuleField; op: CollectionRuleOp; value: string }
export type CollectionSort = 'manual' | 'newest' | 'priceAsc' | 'priceDesc' | 'title' | 'bestselling'

export type OfferCollection = {
  id: string
  title: string
  slug: string
  description: string
  image: string
  kind: 'manual' | 'auto'
  match: 'all' | 'any'
  rules: CollectionRule[]
  sort: CollectionSort
  sortOrder: number
  /** Las ofertas que entran hoy, en su orden. */
  offerIds: string[]
  count: number
  createdAt: string
}

export type CollectionInput = {
  title: string
  slug?: string
  description: string
  image: string
  kind: 'manual' | 'auto'
  match: 'all' | 'any'
  rules: CollectionRule[]
  sort: CollectionSort
  offerIds: string[]
}

export function listCollections(projectId: string, token: string) {
  return request<OfferCollection[]>(`/api/projects/${projectId}/collections`, token)
}

export function createCollection(projectId: string, input: CollectionInput, token: string) {
  return request<OfferCollection>(`/api/projects/${projectId}/collections`, token, { method: 'POST', body: JSON.stringify(input) }).then(staleLabels(projectId))
}

export function updateCollection(projectId: string, collectionId: string, input: Partial<CollectionInput>, token: string) {
  return request<OfferCollection>(`/api/projects/${projectId}/collections/${collectionId}`, token, { method: 'PATCH', body: JSON.stringify(input) }).then(staleLabels(projectId))
}

export function deleteCollection(projectId: string, collectionId: string, token: string) {
  return request<{ ok: boolean }>(`/api/projects/${projectId}/collections/${collectionId}`, token, { method: 'DELETE' })
}

/** Qué ofertas entrarían, sin guardar: la vista previa del diálogo. */
export function previewCollection(projectId: string, input: Partial<CollectionInput>, token: string) {
  return request<{ offerIds: string[] }>(`/api/projects/${projectId}/collections/preview`, token, { method: 'POST', body: JSON.stringify(input) })
}

export function createOffer(projectId: string, draft: OfferDraft, token: string) {
  return request<Offer>(`/api/projects/${projectId}/commerce/offers`, token, {
    method: 'POST',
    body: JSON.stringify(draft),
  }).then(changedAttention(projectId)).then(staleLabels(projectId))
}

/**
 * Los campos de agenda y de página tal y como los guarda el worker: planos.
 *
 * El DTO los devuelve agrupados en `booking` y `bookingPage`, que es como se
 * leen; al escribir van sueltos porque son columnas de `offers`. Traducir aquí
 * es más barato que aceptar las dos formas en el servidor.
 */
export type OfferBookingPatch = {
  bookingDurationMinutes: number | null
  bookingBufferBeforeMinutes: number
  bookingBufferAfterMinutes: number
  bookingStepMinutes: number | null
  bookingCapacity: number
  bookingMinNoticeMinutes: number
  bookingMaxAdvanceDays: number
  bookingRefundPercent: number
  bookingRefundLatePercent: number
  bookingRefundAuto: boolean
  bookingCancelWindowHours: number
  bookingRescheduleWindowHours: number | null
  bookingReminderEnabled: boolean
  bookingReminderHours: number
  bookingLatePolicy: 'contact' | 'move' | 'none'
  bookingLateRefillRefund: boolean
  bookingLateCredit: boolean
  bookingLateForgiveFirst: boolean
  bookingContactEmail: string
  bookingContactPhone: string
  bookingPaymentMode: Offer['booking']['paymentMode']
  bookingDepositAmount: number | null
  bookingResourceId: string | null
  bookingResourceIds: string[]
  bookingMaxParty: number
  bookingPricePer: 'person' | 'booking'
  bookingUnit: 'time' | 'day'
  bookingDaysMin: number
  bookingDaysMax: number
  bookingSpotsDisplay: 'all' | 'low' | 'none'
  bookingAssignment: BookingAssignment
  bookingPageLogo: string | null
  bookingPageAccent: string | null
  bookingPageTheme: Offer['bookingPage']['theme']
  /** La lista entera: es una sola columna, no se parchea por posición. */
  bookingFormFields: BookingFormField[]
  /** Enlace a los términos. Cadena vacía o null los quita. */
  bookingTermsUrl: string | null
}

/** Y los de la página pública, planos por lo mismo: son columnas de `offers`. */
export type OfferPagePatch = {
  pageSeoTitle: string
  pageSeoDescription: string
  pageCanonicalUrl: string
  pageOgSameAsSeo: boolean
  pageOgTitle: string
  pageOgDescription: string
  pageOgImage: string
  pageIndexable: boolean
  pageInSitemap: boolean
}

/**
 * Borra una oferta de verdad.
 *
 * Sus precios se van con ella; los cobros y las citas que ya existan se quedan
 * —con el título copiado dentro— para que el historial siga cuadrando.
 * `keptBookings` son las citas futuras en pie que quedan sin servicio.
 */
export function deleteOffer(projectId: string, offerId: string, token: string) {
  return request<{ deleted: true; title: string; keptBookings: number }>(
    `/api/projects/${projectId}/commerce/offers/${offerId}`,
    token,
    { method: 'DELETE' },
  ).then(changedAttention(projectId))
}

/**
 * Edita una oferta.
 *
 * Sin `type`, y a propósito: el tipo se elige al crearla —ver `OfferDraft`— y
 * no se cambia después. Un producto digital lleva colgando los archivos que
 * entrega y un servicio no entrega nada, así que convertir uno en otro deja esos
 * archivos huérfanos y cambia lo que ya compró alguien. El servidor sigue
 * aceptando la columna; lo que no hay es desde dónde mandarla.
 *
 * Con `slug`, en cambio, sí: es la dirección de la oferta y se puede querer otra
 * —más corta, con la palabra que se anuncia—. El servidor la normaliza y la hace
 * única dentro del proyecto (`uniqueSlug`), así que lo que se manda es una
 * PETICIÓN: puede volver con un sufijo si esa dirección ya estaba ocupada, y hay
 * que pintar lo que conteste, no lo que se escribió.
 */
/** Los campos personalizados de cada tipo de oferta. Ver `OfferFields` en @webbuilder/schemas. */
export function getOfferFields(projectId: string, token: string) {
  return request<{ fields: OfferFields }>(`/api/projects/${projectId}/commerce/offer-fields`, token)
}

/** Guarda la lista entera de campos de UN tipo. */
export function saveOfferFields(projectId: string, kind: OfferTemplateKind, fields: OfferCustomField[], token: string) {
  return request<{ fields: OfferFields }>(`/api/projects/${projectId}/commerce/offer-fields`, token, {
    method: 'PUT',
    body: JSON.stringify({ kind, fields }),
  })
}

export function updateOffer(
  projectId: string,
  offerId: string,
  patch: Partial<Pick<
    Offer,
    'title' | 'description' | 'shortDescription' | 'images' | 'slug' | 'status' | 'thankYouMode' | 'thankYouPageId' | 'thankYouUrl' | 'stock'
  >> & Partial<OfferBookingPatch> & Partial<OfferPagePatch> & {
    /** El aspecto de su página, entero: es una sola columna. Aparte de
     *  `OfferPagePatch` porque aquél es el borrador de la pestaña «Página». */
    pageStyle?: OfferPageStyle
    /** Los campos personalizados, enteros: lo que no venga se queda vacío. */
    customValues?: Record<string, string>
    /** Etiquetas libres: las usan las reglas de las colecciones. */
    tags?: string[]
    /** La marca: también la usan las reglas de las colecciones. */
    brand?: string
    compareAtPrice?: number | null
    /* El aviso al negocio. Plano por lo mismo que la agenda: el DTO lo devuelve
       agrupado en `notify`, que es como se lee, pero son dos columnas. */
    salesNotifyEnabled?: boolean
    salesNotifyEmail?: string
    salesNotifyTo?: 'list' | 'person'
    /** Sólo para quitarla: lo manda el asistente al terminar de completarla. */
    setupPending?: false
  },
  token: string,
) {
  const saved = request<Offer>(`/api/projects/${projectId}/commerce/offers/${offerId}`, token, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
  /* Sólo cuando cambia el estado: es lo que decide si el proyecto «vende». Un
     cambio de título no mueve el aviso y no merece otra consulta. */
  /* Con marca o etiquetas nuevas, la lista compartida se vuelve a pedir. */
  const labelled = patch.brand !== undefined || patch.tags !== undefined ? saved.then(staleLabels(projectId)) : saved
  return patch.status !== undefined ? labelled.then(changedAttention(projectId)) : labelled
}

/**
 * La página pública de una oferta, como la sirve el worker, para la vista previa
 * de «Apariencia». Sin aspecto aplicado: lo pone encima el panel, sin guardar.
 */
export function getOfferPagePreview(projectId: string, offerId: string, token: string) {
  /* `hasSiteNav` / `hasSiteFooter`: si el sitio publicado tiene barra y pie.
     El HTML los trae siempre; el panel los esconde si la oferta los apaga. */
  return request<{ html: string; hasSiteNav?: boolean; hasSiteFooter?: boolean }>(`/api/projects/${projectId}/commerce/offers/${offerId}/page-preview`, token)
}

/**
 * Guarda las variantes de un producto físico: las opciones y lo de cada
 * combinación, todo de una vez. Sin opciones, el producto deja de tener
 * variantes.
 */
export function saveOfferVariants(
  projectId: string,
  offerId: string,
  input: { options: OfferVariantOption[]; variants: Pick<OfferVariant, 'options' | 'stock' | 'sku' | 'active' | 'price' | 'image' | 'compareAtPrice' | 'cost' | 'barcode'>[] },
  token: string,
) {
  return request<Offer>(`/api/projects/${projectId}/commerce/offers/${offerId}/variants`, token, {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

/** Repone existencias de unas variantes desde la ficha, sin tocar nada más. */
export function setOfferVariantStock(projectId: string, offerId: string, variants: { id: string; stock: number | null }[], token: string) {
  return request<Offer>(`/api/projects/${projectId}/commerce/offers/${offerId}/variants/stock`, token, {
    method: 'PATCH',
    body: JSON.stringify({ variants }),
  })
}

/**
 * Añade un precio. Cambiar el importe de uno existente es esto mismo: los
 * precios de Stripe son inmutables, así que el nuevo sustituye al anterior.
 */
export function addOfferPrice(
  projectId: string,
  offerId: string,
  price: { unitAmount: number; currency: string; interval: OfferPrice['interval']; intervalCount?: number; isDefault?: boolean },
  token: string,
) {
  return request<Offer>(`/api/projects/${projectId}/commerce/offers/${offerId}/prices`, token, {
    method: 'POST',
    body: JSON.stringify(price),
  })
}

/**
 * Sube un entregable. Multipart, como las imágenes: es un archivo.
 *
 * Devuelve la oferta entera, igual que los precios: el panel reemplaza su copia
 * con lo que conteste el servidor en vez de adivinar cómo quedó.
 */
export async function uploadOfferFile(projectId: string, offerId: string, file: File, token: string): Promise<Offer> {
  const form = new FormData()
  form.append('file', file)
  /* A pelo y no por `request`: aquél pone `Content-Type: application/json` a
     todo, y en multipart la cabecera la tiene que escribir el navegador —lleva
     el separador de las partes, que nosotros no conocemos—. Es el mismo camino
     que `uploadAsset`, con su reintento tras refrescar el token. */
  const send = (authToken: string) => fetch(
    `${WORKER_URL}/api/projects/${projectId}/commerce/offers/${offerId}/files`,
    { method: 'POST', headers: { Authorization: `Bearer ${authToken}` }, body: form },
  )

  let token_ = (await freshToken(token)) ?? token
  let res = await send(token_)
  if (res.status === 401) {
    const refreshed = await forceRefresh()
    if (refreshed) { token_ = refreshed; res = await send(refreshed) }
  }
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new Error(`${res.status}: ${body}`)
  }
  return ((await res.json()) as { data: Offer }).data
}

/**
 * Renombra un entregable.
 *
 * El nombre es lo que ve quien compra al descargarlo, y lo que llega del
 * navegador es como se llamara en el escritorio de quien lo subió. Sólo cambia
 * la fila: el objeto guardado no lleva el nombre en su clave.
 */
export function renameOfferFile(projectId: string, offerId: string, fileId: string, name: string, token: string) {
  return request<Offer>(`/api/projects/${projectId}/commerce/offers/${offerId}/files/${fileId}`, token, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  })
}

export function removeOfferFile(projectId: string, offerId: string, fileId: string, token: string) {
  return request<Offer>(`/api/projects/${projectId}/commerce/offers/${offerId}/files/${fileId}`, token, {
    method: 'DELETE',
  })
}

export function removeOfferPrice(projectId: string, offerId: string, priceId: string, token: string) {
  return request<Offer>(`/api/projects/${projectId}/commerce/offers/${offerId}/prices/${priceId}`, token, {
    method: 'DELETE',
  })
}

/** Retirar una oferta la archiva: sus cobros siguen necesitando que exista. */
export function archiveOffer(projectId: string, offerId: string, token: string) {
  return request<{ status: Offer['status'] }>(`/api/projects/${projectId}/commerce/offers/${offerId}`, token, {
    method: 'DELETE',
  }).then(changedAttention(projectId))
}

// ── Agenda ───────────────────────────────────────────────────────────────────

/** Una franja de la semana que se repite. Minutos desde medianoche, hora local. */
export type BookingRule = {
  id?: string
  /** 0 = domingo. */
  weekday: number
  startMinute: number
  endMinute: number
}

/** Lo que rompe la semana un día concreto. */
export type BookingException = {
  id?: string
  /** `YYYY-MM-DD` local. */
  day: string
  kind: 'closed' | 'open'
  startMinute: number | null
  endMinute: number | null
  note: string
}

/** Quién presta el servicio. Casi siempre hay uno solo y no se nombra. */
/** Un calendario externo (Google, Outlook, Apple) que bloquea horarios. */
export type CalendarFeed = {
  id: string
  name: string
  /** Nula: bloquea todas las agendas. */
  resourceId: string | null
  /** Sólo el dominio: la dirección completa es secreta y no sale del servidor. */
  host: string
  lastSyncedAt: string | null
  /** `not-ical` | `not-found` | `timeout` | `too-large` | `unreachable` | `secret`, o nulo. */
  lastError: string | null
  busyCount: number
  createdAt: string
}

/** La cuenta de Google Calendar conectada. Ver `googleCalendar.ts` en el worker. */
export type GoogleCalendarAccount = {
  id: string
  email: string
  /** Nula: vale para todas las agendas. */
  resourceId: string | null
  /** Escribir las citas en su calendario. */
  writeEvents: boolean
  /** Y leer sus horas ocupadas para no ofrecerlas. */
  blockBusy: boolean
  /** `reconnect` | `forbidden` | `unreachable`, o nulo si va bien. */
  lastError: string | null
  createdAt: string
}

export type CalendarSyncState = {
  /** El enlace con las citas, o nulo si no se ha creado. */
  exportUrl: string | null
  feeds: CalendarFeed[]
  /** Si la plataforma tiene configurada la conexión con Google. */
  googleReady: boolean
  googleAccounts: GoogleCalendarAccount[]
}

/** A dónde mandar a quien conecta su Google Calendar. */
export function startGoogleCalendar(projectId: string, resourceId: string | null, token: string) {
  return request<{ url: string }>(`/api/projects/${projectId}/calendar-sync/google/start`, token, {
    method: 'POST',
    body: JSON.stringify({ resourceId }),
  })
}

export function updateGoogleCalendar(
  projectId: string,
  accountId: string,
  input: { writeEvents: boolean; blockBusy: boolean },
  token: string,
) {
  return request<GoogleCalendarAccount>(`/api/projects/${projectId}/calendar-sync/google/${accountId}`, token, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

export function disconnectGoogleCalendar(projectId: string, accountId: string, token: string) {
  return request<{ id: string }>(`/api/projects/${projectId}/calendar-sync/google/${accountId}`, token, { method: 'DELETE' })
}

export function getCalendarSync(projectId: string, token: string) {
  return request<CalendarSyncState>(`/api/projects/${projectId}/calendar-sync`, token)
}

export function createCalendarExport(projectId: string, token: string) {
  return request<{ exportUrl: string }>(`/api/projects/${projectId}/calendar-sync/export`, token, { method: 'POST' })
}

export function deleteCalendarExport(projectId: string, token: string) {
  return request<{ exportUrl: null }>(`/api/projects/${projectId}/calendar-sync/export`, token, { method: 'DELETE' })
}

export function addCalendarFeed(projectId: string, input: { name: string; url: string; resourceId: string | null }, token: string) {
  return request<CalendarFeed>(`/api/projects/${projectId}/calendar-sync/feeds`, token, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function syncCalendarFeed(projectId: string, feedId: string, token: string) {
  return request<CalendarFeed>(`/api/projects/${projectId}/calendar-sync/feeds/${feedId}/sync`, token, { method: 'POST' })
}

export function removeCalendarFeed(projectId: string, feedId: string, token: string) {
  return request<{ id: string }>(`/api/projects/${projectId}/calendar-sync/feeds/${feedId}`, token, { method: 'DELETE' })
}

export type BookingResource = {
  id: string
  name: string
  /** El puesto que enseña la página de reserva. Opcional: un worker viejo no lo manda. */
  role?: string
  photoUrl?: string | null
  /** Para los avisos de sus citas. Opcional: un worker viejo no lo manda. */
  email?: string
  /** Nulo = la del proyecto. */
  timezone: string | null
  active: boolean
  sortOrder: number
  rules: BookingRule[]
  exceptions: BookingException[]
}

export type BookingSchedule = {
  /** La del proyecto, a la que caen los recursos sin zona propia. */
  timezone: string
  resources: BookingResource[]
}

export type BookingAssignment = 'least_busy' | 'order' | 'round_robin'

export type Booking = {
  id: string
  changeSeenAt?: string | null
  offerId: string | null
  offerTitle: string
  resourceId: string
  orderId: string | null
  /** ISO absoluto. La hora que se enseña se calcula con `timezone`. */
  startsAt: string
  endsAt: string
  /** La zona en que se reservó, congelada al reservar. */
  timezone: string
  status: 'held' | 'confirmed' | 'cancelled' | 'completed' | 'no_show'
  seatIndex: number
  /** Cuántas personas apunta. Opcional: un worker viejo no lo manda. */
  partySize?: number
  customerName: string
  customerEmail: string
  customerPhone: string
  customerNote: string
  /**
   * Lo contestado a las preguntas del servicio, con la etiqueta congelada
   * dentro: renombrar la pregunta no reescribe lo que ya contestó alguien.
   */
  customFields: BookingFieldAnswer[]
  createdAt: string
}

/** Un comentario interno e independiente dentro de una reserva. */
export type BookingNote = {
  id: string
  bookingId: string
  body: string
  authorUserId: string | null
  authorName: string
  authorAvatarUrl: string | null
  createdAt: string
  updatedAt: string
  isMine: boolean
  canManage: boolean
}

/** Un hueco libre. `seatsLeft` sólo dice algo cuando la oferta tiene cupo. */
export type BookingSlot = {
  startsAt: string
  endsAt: string
  seatsLeft: number
}

export function getBookingSchedule(projectId: string, token: string) {
  return request<BookingSchedule>(`/api/projects/${projectId}/bookings/schedule`, token)
}

/** Alguien nuevo en el equipo. Nace sin horario: se le pone en el paso Horario de un servicio. */
export function createBookingResource(
  projectId: string,
  input: { name: string; role?: string; photoUrl?: string | null },
  token: string,
) {
  return request<{ id: string }>(`/api/projects/${projectId}/bookings/resources`, token, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function updateBookingResource(
  projectId: string,
  resourceId: string,
  input: { name?: string; role?: string; email?: string; photoUrl?: string | null; active?: boolean },
  token: string,
) {
  return request<{ id: string }>(`/api/projects/${projectId}/bookings/resources/${resourceId}`, token, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

export function deleteBookingResource(projectId: string, resourceId: string, token: string) {
  return request<{ id: string }>(`/api/projects/${projectId}/bookings/resources/${resourceId}`, token, { method: 'DELETE' })
}

/**
 * Guarda la semana entera de un recurso.
 *
 * Se manda completa y el servidor reemplaza: el panel siempre conoce el estado
 * final, y casar fila a fila sería más código para el mismo resultado.
 */
/**
 * La marca del sitio: el tema del proyecto, leído entero.
 *
 * Devuelve el objeto tal cual lo guarda el editor. El panel lo normaliza con
 * `@webbuilder/theme` —la misma derivación que usa el editor— en vez de fiarse
 * de que venga completo: un proyecto que nunca ha pasado por el editor no tiene
 * tema, y uno viejo puede no tener los componentes.
 */
export function getProjectTheme(projectId: string, token: string) {
  return request<{ theme: unknown }>(`/api/projects/${projectId}/theme`, token)
}

/**
 * Retira el sitio de internet.
 *
 * Sólo borra la publicación: las páginas, el contenido y las reservas se quedan
 * donde estaban, y volver a publicar no pide rehacer nada. Es lo contrario de
 * publicar sólo en el sentido de «deja de estar accesible».
 *
 * Publicar NO tiene pareja aquí a propósito: el editor construye el HTML de
 * cada página en el navegador y lo sube antes de avisar al servidor, así que no
 * es una llamada, es un proceso. Retirar sí lo es.
 */
export function unpublishSite(projectId: string, token: string) {
  return request<unknown>(`/api/projects/${projectId}/unpublish`, token, { method: 'POST' })
}

/** Guarda la marca. Sólo toca `data.projectTheme`. */
export function saveProjectTheme(projectId: string, theme: unknown, token: string) {
  return request<{ accent: string; configured: boolean }>(
    `/api/projects/${projectId}/brand`,
    token,
    { method: 'PUT', body: JSON.stringify({ theme }) },
  )
}

/** Una versión de la política de un servicio, tal y como quedó anotada. */
export type PolicyVersion = {
  id: string
  createdAt: string
  policy: {
    cancelWindowHours: number
    rescheduleWindowHours: number
    latePolicy: 'contact' | 'move' | 'none'
    refillRefund: boolean
    credit: boolean
    forgiveFirst: boolean
    contactEmail: string
    text: string
  }
}

/**
 * El historial de políticas de un servicio.
 *
 * Sólo se lee: lo escribe el propio guardado del servicio. No hay forma de
 * añadir ni de borrar una línea desde fuera, que es lo que le da valor.
 */
export function getPolicyHistory(projectId: string, offerId: string, token: string) {
  return request<PolicyVersion[]>(
    `/api/projects/${projectId}/commerce/offers/${offerId}/policy-history`,
    token,
  )
}

export function saveBookingSchedule(
  projectId: string,
  resourceId: string,
  schedule: { rules: BookingRule[]; exceptions: BookingException[] },
  token: string,
) {
  return request<{ resources: BookingResource[] }>(
    `/api/projects/${projectId}/bookings/resources/${resourceId}/schedule`,
    token,
    {
      method: 'PUT',
      body: JSON.stringify({
        rules: schedule.rules.map(({ weekday, startMinute, endMinute }) => ({ weekday, startMinute, endMinute })),
        exceptions: schedule.exceptions.map(({ day, kind, startMinute, endMinute, note }) => ({
          day, kind, startMinute, endMinute, note,
        })),
      }),
    },
  )
}

/**
 * Las tres familias del catálogo, cada una con su índice público.
 *
 * `bookings` reúne los servicios con agenda en `/bookings`, `digital` lo que se
 * descarga en `/digital` y `services` lo que se contrata sin horario en
 * `/services`. Las tres guardan lo mismo y se leen por la misma ruta con la
 * familia dentro; quien las pinta es el worker.
 */
export type CatalogKind = 'bookings' | 'digital' | 'products' | 'services'

/** La dirección de cada índice. La misma tabla que el worker; ver `catalogIndexPage.ts`. */
export const CATALOG_INDEX_PATH: Record<CatalogKind, string> = {
  bookings: '/bookings',
  digital: '/digital',
  products: '/products',
  services: '/services',
}

/**
 * Lo que se puede tocar de un índice público.
 *
 * Dos grupos, y así se enseñan: si se ve o no, y cómo se ve. Todo lo de aspecto
 * nace vacío, y vacío significa «lo del sitio» — el color de la marca, su
 * fondo—, que es lo que hace que un proyecto que nunca abra esa pantalla tenga
 * igualmente una página que se le parece. El contrato lo manda el worker; ver
 * `catalogIndexPage.ts`.
 */
export type CatalogPageSettings = {
  enabled: boolean
  title: string
  subtitle: string
  /** Tarjetas por fila en pantalla ancha. `0` = las que quepan. */
  columns: 0 | 1 | 2 | 3
  /*
   * Todo lo de aspecto son IDS DE TOKEN del tema del editor, no valores.
   *
   * Se guarda «card-soft» y no el fondo y el radio que tiene hoy: la página se
   * resuelve contra el tema cada vez que se sirve, así que un cambio en el
   * editor la cambia también. Vacío = el token de siempre del sitio.
   */
  /** Color de fondo: id de un color del tema (`background`, `surface`…). */
  background: string
  /** Color de la letra: id de un color del tema. */
  text: string
  /** Tarjeta: id de un estilo de tarjeta (`card-default`, `card-soft`…). */
  card: string
  /** Botón: id de un estilo de botón (`button-primary`…). */
  button: string
  /** Tipografías: ids de estilos de texto (`h1`, `h3`, `p`…). */
  titleStyle: string
  cardTitleStyle: string
  bodyStyle: string
  /** El precio de cada tarjeta: id de un estilo de texto (`h5` por defecto). */
  priceStyle: string
  /**
   * La barra y el pie del sitio arriba y abajo de la página. Encendidos por
   * defecto; opcionales porque un worker anterior no los manda.
   */
  showNav?: boolean
  showFooter?: boolean
}

export function getCatalogPageSettings(projectId: string, kind: CatalogKind, token: string) {
  return request<CatalogPageSettings>(`/api/projects/${projectId}/catalog/${kind}/page`, token)
}

export function saveCatalogPageSettings(projectId: string, kind: CatalogKind, settings: CatalogPageSettings, token: string) {
  return request<CatalogPageSettings>(`/api/projects/${projectId}/catalog/${kind}/page`, token, {
    method: 'PATCH',
    body: JSON.stringify(settings),
  })
}

/** Una línea de la bitácora de una cita. */
export type BookingEvent = {
  id: string
  kind: string
  at: string
  /** El nombre de quien lo hizo, copiado al anotarlo. Vacío si no fue una persona. */
  actorName: string
  actorUserId: string | null
  actorKind: string
  meta: Record<string, unknown>
}

/**
 * El historial de una cita.
 *
 * Sólo se lee: lo escribe cada acción por su cuenta y no hay forma de tocarlo
 * desde fuera, que es lo que le da valor.
 */
export function listBookingEvents(projectId: string, bookingId: string, token: string) {
  return request<BookingEvent[]>(`/api/projects/${projectId}/bookings/${bookingId}/events`, token)
}

/** Una cita completa, para abrirla desde sitios que sólo cargan su resumen. */
export function getBooking(projectId: string, bookingId: string, token: string) {
  return request<Booking>(`/api/projects/${projectId}/bookings/${bookingId}`, token)
}

export function listBookingNotes(projectId: string, bookingId: string, token: string) {
  return request<{ notes: BookingNote[]; canWrite: boolean }>(`/api/projects/${projectId}/bookings/${bookingId}/notes`, token)
}

export function createBookingNote(projectId: string, bookingId: string, body: string, token: string) {
  return request<BookingNote>(`/api/projects/${projectId}/bookings/${bookingId}/notes`, token, {
    method: 'POST',
    body: JSON.stringify({ body }),
  })
}

export function updateBookingNote(projectId: string, bookingId: string, noteId: string, body: string, token: string) {
  return request<BookingNote>(`/api/projects/${projectId}/bookings/${bookingId}/notes/${noteId}`, token, {
    method: 'PATCH',
    body: JSON.stringify({ body }),
  })
}

export function deleteBookingNote(projectId: string, bookingId: string, noteId: string, token: string) {
  return request<{ ok: boolean }>(`/api/projects/${projectId}/bookings/${bookingId}/notes/${noteId}`, token, { method: 'DELETE' })
}

export function getActiveBookingCount(projectId: string, token: string) {
  return request<{ count: number }>(`/api/projects/${projectId}/bookings/active-count`, token)
}

export function getLatestDashboardActivity(projectId: string, token: string) {
  return request<Record<'contacts' | 'forms' | 'orders' | 'bookings', string | null>>(`/api/projects/${projectId}/activity-latest`, token)
}

export function listBookings(projectId: string, range: { from: string; to: string }, token: string) {
  return request<Booking[]>(
    `/api/projects/${projectId}/bookings?from=${range.from}&to=${range.to}`,
    token,
  )
}

export function listBookingSlots(
  projectId: string,
  query: { offerId: string; from: string; to: string; resourceId?: string; party?: number; days?: number },
  token: string,
) {
  const extra = `${query.resourceId ? `&resourceId=${query.resourceId}` : ''}${query.party && query.party > 1 ? `&party=${query.party}` : ''}${query.days ? `&days=${query.days}` : ''}`
  return request<{ timezone: string; resourceId: string; slots: BookingSlot[] }>(
    `/api/projects/${projectId}/bookings/availability?offerId=${query.offerId}&from=${query.from}&to=${query.to}${extra}`,
    token,
  )
}

export function createBooking(
  projectId: string,
  booking: {
    offerId?: string | null
    resourceId?: string
    partySize?: number
    /** Por días: cuántos. */
    days?: number
    startsAt: string
    durationMinutes?: number
    title?: string
    customerName?: string
    customerEmail?: string
    customerPhone?: string
    customerNote?: string
  },
  token: string,
) {
  return request<Booking>(`/api/projects/${projectId}/bookings`, token, {
    method: 'POST',
    body: JSON.stringify(booking),
  }).then((booking) => { notifyBookingsChanged(projectId); return booking })
}

export function updateBooking(
  projectId: string,
  bookingId: string,
  patch: Partial<Pick<Booking, 'status' | 'startsAt' | 'endsAt' | 'customerNote' | 'resourceId'>>,
  token: string,
) {
  return request<Booking>(`/api/projects/${projectId}/bookings/${bookingId}`, token, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  }).then((booking) => { notifyBookingsChanged(projectId); return booking })
}

// ── Blog ─────────────────────────────────────────────────────────────────────
// Las entradas viven en la base, no en el proyecto del editor: guardarlas las
// publica sin republicar el sitio. Ver `routes/blog.ts` en el worker.

export type BlogOverview = {
  settings: BlogSettings | null
  posts: BlogPost[]
  /** Los perfiles de los autores (Blog › Autores): para sus caras en la cabecera. */
  authors?: Array<{ name: string; avatarUrl: string }>
  usage: { published: number; limit: number | null }
}

/** Lo que se manda al guardar: sólo lo que cambió. */
export type BlogPostInput = Partial<{
  title: string
  slug: string
  excerpt: string
  coverImage: string
  coverAlt: string
  content: string
  authorName: string
  categories: string[]
  status: 'draft' | 'published'
  publishedAt: string | null
  seoTitle: string
  seoDescription: string
  seoImage: string
}>

export function getBlog(projectId: string, token: string) {
  return request<BlogOverview>(`/api/projects/${projectId}/blog`, token)
}

export function getBlogPost(projectId: string, postId: string, token: string) {
  return request<BlogPost>(`/api/projects/${projectId}/blog/posts/${postId}`, token)
}

export function createBlogPost(projectId: string, input: BlogPostInput, token: string) {
  return request<BlogPost>(`/api/projects/${projectId}/blog/posts`, token, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function updateBlogPost(projectId: string, postId: string, input: BlogPostInput, token: string) {
  return request<BlogPost>(`/api/projects/${projectId}/blog/posts/${postId}`, token, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })
}

export function deleteBlogPost(projectId: string, postId: string, token: string) {
  return request<{ id: string }>(`/api/projects/${projectId}/blog/posts/${postId}`, token, { method: 'DELETE' })
}

/** Quita una categoría de todas las entradas del blog; devuelve a cuántas se la quitó. */
export function removeBlogCategory(projectId: string, name: string, token: string) {
  return request<{ name: string; posts: number }>(`/api/projects/${projectId}/blog/categories/remove`, token, {
    method: 'POST',
    body: JSON.stringify({ name }),
  })
}

/** Quita un autor de todas las entradas del blog; devuelve a cuántas se lo quitó. */
/** Las marcas y etiquetas del proyecto, con cuántas ofertas llevan cada una. */
export type OfferLabels = { brands: { name: string; count: number }[]; tags: { name: string; count: number }[] }
export function listOfferLabels(projectId: string, token: string) {
  return request<OfferLabels>(`/api/projects/${projectId}/commerce/labels`, token)
}

/** Quita una etiqueta de todas las ofertas y de las condiciones de las colecciones. */
export function removeOfferTag(projectId: string, name: string, token: string) {
  return request<{ name: string; offers: number; collections: number }>(`/api/projects/${projectId}/commerce/tags/remove`, token, {
    method: 'POST',
    body: JSON.stringify({ name }),
  }).then(staleLabels(projectId))
}

/** Quita una marca de todas las ofertas y de las condiciones de las colecciones. */
export function removeOfferBrand(projectId: string, name: string, token: string) {
  return request<{ name: string; offers: number; collections: number }>(`/api/projects/${projectId}/commerce/brands/remove`, token, {
    method: 'POST',
    body: JSON.stringify({ name }),
  }).then(staleLabels(projectId))
}

/** Tras guardar algo que puede llevar marcas o etiquetas: las pantallas las vuelven a pedir. */
function staleLabels(projectId: string) {
  return <T,>(result: T): T => { markOfferLabelsStale(projectId); return result }
}

/** Un autor del blog con su perfil (Blog › Autores) y cuántas entradas firma. */
export type BlogAuthor = { name: string; avatarUrl: string; bio: string; links: { label: string; url: string }[]; posts: number }

export function getBlogAuthors(projectId: string, token: string) {
  return request<{ authors: BlogAuthor[]; ready: boolean }>(`/api/projects/${projectId}/blog/authors`, token)
}

/** Borra un autor: su perfil y su firma en todas las entradas. */
export function deleteBlogAuthor(projectId: string, name: string, token: string) {
  return request<{ name: string; posts: number }>(`/api/projects/${projectId}/blog/authors/delete`, token, {
    method: 'POST',
    body: JSON.stringify({ name }),
  })
}

export function saveBlogAuthor(projectId: string, author: Omit<BlogAuthor, 'posts'> & { previousName?: string }, token: string) {
  return request<{ ok: true }>(`/api/projects/${projectId}/blog/authors`, token, {
    method: 'PUT',
    body: JSON.stringify(author),
  })
}

export function removeBlogAuthor(projectId: string, name: string, token: string) {
  return request<{ name: string; posts: number }>(`/api/projects/${projectId}/blog/authors/remove`, token, {
    method: 'POST',
    body: JSON.stringify({ name }),
  })
}

// ── El asistente del panel ─────────────────────────────────────────────────

/** Lo que puede proponer el asistente. El panel lo ejecuta; el servidor no hace nada. */
export type DashboardAssistAction =
  | { type: 'navigate'; section: string }
  | { type: 'create_offer'; kind: 'services' | 'digital' | 'products' | 'bookings'; title: string; price?: number; description?: string; options?: OfferOptionDraft[]; schedule?: OfferScheduleDraft }
  | { type: 'new_page'; idea?: string }
  | { type: 'write_post'; topic: string }
  | { type: 'query'; query: DashboardQuery; period?: DashboardQueryPeriod }

/** La semana de una cita: días (0 = domingo) y franja en minutos desde medianoche. */
export type OfferScheduleDraft = { days: number[]; startMinute: number; endMinute: number }

/** Una opción de un producto físico: «Talla: L, XL». Con el tono de cada color si lo hay. */
export type OfferOptionDraft = { name: string; values: string[]; swatches?: Record<string, string> }

/** Las preguntas de datos que sabe contestar. Ver `dashboardQueries.ts` en el worker. */
export type DashboardQuery = 'contacts_summary' | 'top_customers' | 'sales_summary' | 'top_offers' | 'upcoming_bookings' | 'visits_summary' | 'unread_forms'
export type DashboardQueryPeriod = 'today' | 'week' | 'month' | 'year' | 'all'

export type DashboardQueryResult = {
  query: DashboardQuery
  period: DashboardQueryPeriod
  values: Record<string, string>
  rows: Array<{ label: string; detail?: string; link?: { section: 'contacts' | 'payments' | 'bookingsCalendar' | 'forms' | 'orders'; id?: string } }>
  empty: boolean
  otherCurrencies?: string[]
}

/** La ficha de una oferta nueva, redactada con el contexto del negocio. No la crea. */
export function dashboardDraftOffer(token: string, input: { projectId: string; kind: 'services' | 'digital' | 'products' | 'bookings'; title: string; price?: number; notes?: string; options?: OfferOptionDraft[]; language: 'es' | 'en' }) {
  return request<{ kind: 'services' | 'digital' | 'products' | 'bookings'; title: string; shortDescription: string; description: string; price?: number; options?: OfferOptionDraft[]; usage?: AiAdminUsage }>('/api/ai/dashboard/draft-offer', token, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/** Ejecuta la pregunta de datos que eligió el asistente. Sin IA. */
export function dashboardQuery(token: string, input: { projectId: string; query: DashboardQuery; period?: DashboardQueryPeriod; language: 'es' | 'en' }) {
  return request<DashboardQueryResult>('/api/ai/dashboard/query', token, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export type DashboardAssistInput = {
  projectId: string
  message: string
  language: 'es' | 'en'
  history: Array<{ role: 'user' | 'assistant'; text: string }>
  destinations: Array<{ id: string; label: string }>
  can: { offers: boolean; pages: boolean; blog: boolean; queries: DashboardQuery[] }
}

/** Qué quiere la persona, cuando el buscador local no lo entendió. Ver `aiDashboard.ts` en el worker. */
/** Los tokens de una respuesta. Sólo llegan a los admins. */
export type AiAdminUsage = { input: number; output: number; thought: number; cached?: boolean }

export function dashboardAssist(token: string, input: DashboardAssistInput) {
  return request<{ reply: string; action: DashboardAssistAction | null; usage?: AiAdminUsage }>('/api/ai/dashboard/assist', token, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/** Una entrada de blog escrita por la IA. El panel la guarda como borrador. */
export function dashboardWritePost(token: string, input: { projectId: string; topic: string; language: 'es' | 'en'; businessName?: string }) {
  return request<{ title: string; excerpt: string; html: string; usage?: AiAdminUsage }>('/api/ai/dashboard/write-post', token, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/**
 * Una oferta en un paso, «por completar»: la misma ruta que el panel Catálogo
 * del editor. Se termina después en su asistente.
 */
export function createQuickOffer(projectId: string, input: { title: string; kind: 'services' | 'digital' | 'products' | 'bookings'; price?: number; description?: string; shortDescription?: string; variantOptions?: OfferOptionDraft[]; schedule?: OfferScheduleDraft }, token: string) {
  return request<{ id: string }>(`/api/projects/${projectId}/commerce/offers/quick`, token, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/** Un correo del worker en la pestaña «Correos» de admin. Ver `adminEmails.ts`. */
export type AdminEmailPreview = {
  id: string
  group: 'orders' | 'store' | 'bookings' | 'forms' | 'customers' | 'domains' | 'account'
  audience: 'customer' | 'owner' | 'staff'
  fn: string
  spanishOnly: boolean
  subject: { es: string; en: string }
}

export function listAdminEmails(token: string) {
  return request<AdminEmailPreview[]>('/api/admin/emails', token)
}

export function getAdminEmail(token: string, id: string, lang: 'es' | 'en') {
  return request<{ subject: string; html: string }>(`/api/admin/emails/${encodeURIComponent(id)}?lang=${lang}`, token)
}

/** La cuota de IA del mes, la misma que el anillo del editor. Ver `ai.ts` en el worker. */
export type AiTokenBudget = {
  plan: 'free' | 'pro' | 'business'
  used: number
  limit: number | null
  percentUsed: number
  exhausted: boolean
}

export function getAiTokenBudget(token: string) {
  return request<AiTokenBudget>('/api/ai/token-budget', token)
}
